import { getFormattedUnit } from "config";
import ExcelJS from "exceljs";

const htmlToText = (html) => {
    if (!html) return "-";
    
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/?p>/gi, '\n')
      .replace(/<\/?div>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim() || "-";
  };

  const applyAutoRowHeight = (
    sheet,
    rowIndex,
    text,
    {
      mergedCols = 3,
      columnWidth = 15,
      minHeight = 22,
      lineHeight = 15,
    } = {}
  ) => {
    if (!text || text === "-") return;
  
    const approxCharsPerLine = Math.floor(columnWidth * mergedCols * 1.2);
    const lineCount = Math.ceil(text.length / approxCharsPerLine);
  
    const row = sheet.getRow(rowIndex);
    row.height = Math.max(minHeight, lineCount * lineHeight);
  };

  const applyOuterBorderToRange = (sheet, lastRow) => {
    try {
      const lastCol = sheet.columnCount || 1;
      
      // Only apply borders if cells exist
      if (lastRow > 0 && lastCol > 0) {
        // Apply borders carefully
        for (let c = 1; c <= lastCol; c++) {
          // Top border
          const topCell = sheet.getRow(1).getCell(c);
          topCell.border = { 
            ...topCell.border, 
            top: { style: 'thin', color: { argb: 'FF000000' } } 
          };
          
          // Bottom border
          const bottomCell = sheet.getRow(lastRow).getCell(c);
          if (bottomCell) {
            bottomCell.border = { 
              ...bottomCell.border, 
              bottom: { style: 'thin', color: { argb: 'FF000000' } } 
            };
          }
        }
      }
    } catch (error) {
      console.warn('Border application error:', error);
    }
  };

  const getRankNumber = (rank) => {
    if (!rank || rank === "NA") return Infinity; // push to last
    const num = parseInt(rank.replace(/\D/g, ""), 10);
    return isNaN(num) ? Infinity : num;
  };

  const hasValidBudget = (rfqData) => {
    const budget = rfqData?.rfqBudget;
    return (
      typeof budget === "number" &&
      !isNaN(budget) &&
      budget > 0
    );
  };  

  const getLatestQuotationSet = (supplier) =>
    supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0] || {};

  const getCommercialDiscountPercent = (supplier) => {
    const percent = Number(getLatestQuotationSet(supplier)?.discountOnBasicAmountPercent || 0);
    if (!Number.isFinite(percent) || percent <= 0) return 0;
    return Math.min(100, percent);
  };

  const getCommercialDiscountAmount = (supplier) => {
    const amount = Number(getLatestQuotationSet(supplier)?.discountOnBasicAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    return amount;
  };

  const hasCommercialDiscount = (rfqData, suppliers = []) =>
    rfqData?.rfqType === "material" &&
    rfqData?.status === "CLOSED" &&
    suppliers.some((supplier) => getCommercialDiscountPercent(supplier) > 0 || getCommercialDiscountAmount(supplier) > 0);
  
export const handleDownloadComparisonExcel = async (
  rfqData,
  setExcelLoading,
  setLoadingMessage,
  enqueueSnackbar
) => {
  // Include ALL suppliers (both with and without quotations)
  const allSuppliers = rfqData?.suppliers || [];

  // Separate suppliers into two groups: those with quotations and those without
  const suppliersWithQuotations = allSuppliers.filter(
    (supplier) => supplier?.quotationReceived
  );
  const suppliersWithoutQuotations = allSuppliers.filter(
    (supplier) => !supplier?.quotationReceived
  );

  // Sort only the suppliers with quotations by rank
  const sortedSuppliersWithQuotes = [...suppliersWithQuotations].sort(
    (a, b) => {
      const numA = parseInt(a.vendorQuotations?.rank?.substring(1));
      const numB = parseInt(b.vendorQuotations?.rank?.substring(1));
      return numA - numB;
    }
  );

  // Combine sorted suppliers with quotes and unsorted suppliers without quotes
  const sortedSuppliers = [
    ...sortedSuppliersWithQuotes,
    ...suppliersWithoutQuotations,
  ];

  const awardedSuppliersAmount = rfqData?.suppliers
    ?.filter((supplier) => supplier.isAwarded)
    .reduce((total, supplier) => {
      const amount = supplier?.closingDetails?.[0]?.amount || 0;
      return total + amount;
    }, 0);

  const diff = rfqData?.rfqBudget - awardedSuppliersAmount;
  const isOverBudget = diff < 0;
  const formattedDiff = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
  }).format(Math.abs(diff));
  const budgetDeviationText = `Budget Deviation: Rs. ${formattedDiff || "NA"} ${
    isOverBudget ? "▲" : "▼"
  }`;

  try {
    setExcelLoading(true);
    setLoadingMessage("Generating Comparison Excel...");

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Detailed_View");

    const setNumericCell = (cell, value) => {
      cell.value = value ?? 0;
      cell.numFmt = "#,##0.00";
      cell.alignment = { horizontal: "right", vertical: "middle" };
    };

    const formatDate = (dateString) => {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB"); // Formats as DD/MM/YYYY
    };

    const calculatePercentageDifference = (firstPrice, latestPrice) => {
      if (!firstPrice || firstPrice === 0) return 0;
      return latestPrice - firstPrice;
    };

    sheet.columns = [
      { header: "Sr.", key: "sr", width: 5 },
      { header: "Material Name", key: "materialName", width: 35 },
      { header: "Specification", key: "specification", width: 40 },
      { header: "Approved Brands", key: "brands", width: 20 },
      { header: "Qty", key: "quantity", width: 10 },
      { header: "Unit", key: "unit", width: 10 },
      { header: "Last Rate", key: "lastRate", width: 15 },
      { header: "Last Rate Date", key: "lastRateDate", width: 15 },
      { header: "Lowest Rate", key: "lowestRate", width: 15 },
    ];
    let budgetText = "";

    if (hasValidBudget(rfqData)) {
      budgetText =
        `RFQ Budget: Rs. ${new Intl.NumberFormat("en-IN").format(
          rfqData.rfqBudget
        )}\n` +
        `${budgetDeviationText}\n`;
    }
    
    const rfqHeaderRange = "A1:I1";
    sheet.mergeCells(rfqHeaderRange);
    sheet.getCell("A1").value =
      `Project Name: ${rfqData.projectName}\n` +
      `Project Location: ${rfqData.location}\n` +
      `Neevay RFQ ID: ${rfqData.rfqId}\n` +
      `RFQ Date: ${formatDate(rfqData.createdAt)}\n` +
      `Neevay RFQ Subject: ${rfqData.name}\n` +
      `Total Quotations: ${suppliersWithQuotations.length}\n` +
      `Total Invited Vendors: ${allSuppliers.length}\n` + // Added line to show total invited vendors
      budgetText;
    sheet.getCell("A1").alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true,
    };
    sheet.getCell("A1").font = { bold: true };

    sheet.getRow(2).values = [
      "Sr.",
      "Material Name",
      "Specification",
      "Approved Brands",
      "Qty",
      "Unit",
      "Last Rate",
      "Last Rate Date",
      "Lowest Rate",
    ];
    sheet.getRow(2).font = { bold: true };
    sheet.getRow(2).alignment = { vertical: "middle", horizontal: "center" };

    const lowestPrices = {};
    rfqData?.materials?.forEach((material) => {
      let minPrice = Infinity;
      let minSupplierIndexes = [];

      sortedSuppliersWithQuotes?.forEach((supplier, vendorIndex) => {
        const vendorQuotation =
          supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
        const vendorItem = vendorQuotation?.materialDetails?.find(
          (item) => item.id === material._id
        );

        if (vendorItem && vendorItem.basePrice > 0) {
          const discountedPrice =
            vendorItem.basePrice -
            (vendorItem.basePrice * vendorItem.discount) / 100;
          if (discountedPrice > 0) {
            if (discountedPrice < minPrice) {
              minPrice = discountedPrice;
              minSupplierIndexes = [vendorIndex];
            } else if (discountedPrice === minPrice) {
              minSupplierIndexes.push(vendorIndex);
            }
          }
        }
      });

      lowestPrices[material._id] = {
        minPrice: minPrice === Infinity ? 0 : minPrice,
        minSupplierIndexes,
      };
    });

    rfqData.materials.forEach((material, index) => {
      const row = sheet.getRow(index + 3);
      row.getCell(1).value = index + 1;
      row.getCell(2).value = material.name.replace(/<[^>]*>/g, "");
      row.getCell(3).value = material.specifications.replace(/<[^>]*>/g, "");
      row.getCell(4).value = material.brands.join(", ");
      setNumericCell(row.getCell(5), Number(material.quantity));
      row.getCell(6).value = getFormattedUnit(material.unit);

      // Last Rate
      if (material.lastRate) {
        setNumericCell(row.getCell(7), material.lastRate);
      } else {
        row.getCell(7).value = "-";
        row.getCell(7).alignment = { horizontal: "center" };
      }

      // Last Rate Date
      row.getCell(8).value = formatDate(material.lastRateDate);
      row.getCell(8).alignment = { horizontal: "center" };

      // Lowest Rate
      setNumericCell(row.getCell(9), lowestPrices[material._id]?.minPrice ?? 0);
      row.getCell(9).style = {
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFBBF7B3" },
        },
        alignment: {
          horizontal: "right",
          vertical: "middle",
        },
        border: {
          bottom: { style: "thin" },
        },
      };
    });

    let startCol = 10;
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = startCol + supplierIndex * 8;
      const colEnd = colStart + 7;
      const colLetter = sheet.getColumn(colStart).letter;
      const supplierHeaderRange = `${colLetter}1:${
        sheet.getColumn(colEnd).letter
      }1`;
      sheet.mergeCells(supplierHeaderRange);
      let budgetDeviationLine = "";

      if (hasValidBudget(rfqData) && supplier.quotationReceived) {
        const deviation =
          rfqData.rfqBudget -
          (supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0);

        budgetDeviationLine = `Budget Deviation: Rs. ${new Intl.NumberFormat(
          "en-IN"
        ).format(Math.abs(deviation))} ${
          deviation < 0 ? "▲" : "▼"
        }\n\n`;
      }

      // Different header for suppliers without quotations
      const supplierDetails = supplier.quotationReceived
        ? `Vendor Rank: ${supplier.vendorQuotations?.rank || "N/A"}\n` +
          `Vendor Name: ${supplier.businessName}\n` +
          `Vendor Phone: ${supplier.contactDetails[0]?.mobile}\n` +
          `Vendor Email: ${supplier.contactDetails[0]?.email}\n` +
          budgetDeviationLine
        : `Vendor Name: ${supplier.businessName}\n` +
          `Vendor Phone: ${supplier.contactDetails[0]?.mobile}\n` +
          `Vendor Email: ${supplier.contactDetails[0]?.email}\n` +
          `Status: Did Not Submit Quotation\n\n`;

      sheet.getCell(`${colLetter}1`).value = supplierDetails;
      sheet.getCell(`${colLetter}1`).alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
      };
      sheet.getCell(`${colLetter}1`).font = { bold: true };

      // Header row
      sheet.getRow(2).getCell(colStart).value = "MRP";
      sheet.getRow(2).getCell(colStart + 1).value = "Discount";
      sheet.getRow(2).getCell(colStart + 2).value = "Net Rate";
      sheet.getRow(2).getCell(colStart + 3).value = "First Rate";
      sheet.getRow(2).getCell(colStart + 4).value = "Negotiation";
      sheet.getRow(2).getCell(colStart + 5).value = "GST";
      sheet.getRow(2).getCell(colStart + 6).value = "Basic Amount";
      sheet.getRow(2).getCell(colStart + 7).value = "Vendor Remarks";

      // Set column widths
      [10, 9, 13, 13, 13, 8, 17, 35].forEach(
        (w, i) => (sheet.getColumn(colStart + i).width = w)
      );

      rfqData.materials.forEach((material, index) => {
        const row = sheet.getRow(index + 3);

        if (supplier.quotationReceived) {
          const lastQuotationSet =
            supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
          const firstQuotationSet =
            supplier.vendorQuotations?.quotationSets?.[0];
          const quotedMaterial = lastQuotationSet?.materialDetails?.find(
            (m) => m.id === material._id
          );
          const firstQuotedMaterial = firstQuotationSet?.materialDetails?.find(
            (m) => m.id === material._id
          );

          if (quotedMaterial) {
            // MRP (Base Price)
            setNumericCell(row.getCell(colStart), quotedMaterial.basePrice);

            // Discount
            const discount = parseFloat(quotedMaterial.discount) || 0;
            setNumericCell(row.getCell(colStart + 1), discount);
            row.getCell(colStart + 1).numFmt = "0.00%";
            row.getCell(colStart + 1).value = discount / 100;

            // Net Rate
            const netRate =
              quotedMaterial.basePrice -
              (quotedMaterial.basePrice * quotedMaterial.discount) / 100;
            setNumericCell(row.getCell(colStart + 2), netRate);

            // First Rate
            const firstSupplierPrice = firstQuotedMaterial
              ? firstQuotedMaterial.basePrice -
                (firstQuotedMaterial.basePrice * firstQuotedMaterial.discount) /
                  100
              : 0;
            setNumericCell(
              row.getCell(colStart + 3),
              firstSupplierPrice > 0 ? firstSupplierPrice : "-"
            );

            // Negotiation
            const percentageDifference = calculatePercentageDifference(
              firstSupplierPrice,
              netRate
            );
            const negotiationCell = row.getCell(colStart + 4);
            negotiationCell.value = Math.abs(percentageDifference);
            const displayDifference = Math.abs(percentageDifference);
            setNumericCell(negotiationCell, displayDifference);

            if (percentageDifference < 0) {
              negotiationCell.font = { color: { argb: "FF00AA00" } };
            } else if (percentageDifference > 0) {
              negotiationCell.font = { color: { argb: "FFFF0000" } };
            }

            // GST
            const gst = parseFloat(quotedMaterial.gst) || 0;
            row.getCell(colStart + 5).value = gst / 100;
            row.getCell(colStart + 5).numFmt = "0.00%";
            row.getCell(colStart + 5).alignment = { horizontal: "right" };

            // Basic Amount (Price Without GST)
            setNumericCell(
              row.getCell(colStart + 6),
              quotedMaterial.priceWithoutGst
            );

            // Vendor Remarks
            row.getCell(colStart + 7).value = quotedMaterial.remark.replace(
              /<[^>]*>/g,
              ""
            );
            row.getCell(colStart + 7).alignment = { wrapText: true };

            // Highlight if lowest price
            const isLowest =
              lowestPrices[material._id]?.minSupplierIndexes.includes(
                supplierIndex
              );
            if (isLowest) {
              row.getCell(colStart + 2).style = {
                fill: {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFBBF7B3" },
                },
                alignment: {
                  horizontal: "right",
                  vertical: "middle",
                },
              };
            }
          }
        } else {
          // For vendors who didn't submit quotations, leave all cells empty with "-"
          for (let i = 0; i < 8; i++) {
            row.getCell(colStart + i).value = "-";
            row.getCell(colStart + i).alignment = { horizontal: "center" };
          }
        }
      });

      const lineCount = supplierDetails.split("\n").length;
      sheet.getRow(1).height = Math.max(20, lineCount * 22);
    });

    const materialCostRowIndex = rfqData.materials.length + 3 + 1;
    const materialCostRow = sheet.getRow(materialCostRowIndex);
    sheet.mergeCells(`A${materialCostRowIndex}:I${materialCostRowIndex}`);
    materialCostRow.getCell(1).value = "Material Cost:";
    materialCostRow.getCell(1).font = { bold: true };
    materialCostRow.getCell(1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 10 + supplierIndex * 8; // Changed from 6 to 8
      const cost =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
          ?.totalWithoutGst ?? 0;
      setNumericCell(materialCostRow.getCell(colStart + 6), cost); // Changed from +4 to +6
    });

    let commercialDiscountRowOffset = 0;
    if (hasCommercialDiscount(rfqData, sortedSuppliers)) {
      commercialDiscountRowOffset = 1;
      const rowIndex = materialCostRowIndex + commercialDiscountRowOffset;
      const row = sheet.getRow(rowIndex);
      sheet.mergeCells(`A${rowIndex}:I${rowIndex}`);
      row.getCell(1).value = "Commercial Discount:";
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = 10 + supplierIndex * 8;
        const percent = getCommercialDiscountPercent(supplier);
        const amount = getCommercialDiscountAmount(supplier);
        const cell = row.getCell(colStart + 6);

        cell.value = amount > 0
          ? `Rs. ${new Intl.NumberFormat("en-IN", {
              maximumFractionDigits: 2,
            }).format(amount)}${percent > 0 ? ` (${percent}%)` : ""}`
          : "-";
        cell.alignment = { horizontal: "right", vertical: "middle" };
      });
    }

    // Now Add "Other Charges"
    const chargeLabels = [
      { label: "Transport Charges :", key: "transport" },
      { label: "Mathade Charges :", key: "mathadeCharges" },
      { label: "Loading Charges :", key: "loading" },
      { label: "Unloading Charges :", key: "unLoading" },
      { label: "Miscellaneous Charges :", key: "miscCharges" },
    ];
    const enabledCharges = chargeLabels.filter(
      (charge) => rfqData?.otherCharges?.[charge.key]
    );

    enabledCharges.forEach((charge, index) => {
      const rowIndex = materialCostRowIndex + commercialDiscountRowOffset + index + 1;
      const row = sheet.getRow(rowIndex);
      sheet.mergeCells(`A${rowIndex}:I${rowIndex}`);
      row.getCell(1).value = charge.label;
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = 10 + supplierIndex * 8; // Changed from 6 to 8
        const chargeMRP =
          supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
            ?.otherCharges?.[charge.key]?.mrp ?? 0;
        setNumericCell(row.getCell(colStart + 6), chargeMRP); // Changed from +4 to +6
      });
    });

    // Add Summary Rows (Basic Amount, GST, Total, Payment Terms)
    const summaryStartRowIndex =
      materialCostRowIndex + commercialDiscountRowOffset + enabledCharges.length + 1;
    const supplierDetailsLabels = [
      {
        label: "Total Basic Amount:",
        getValue: (s) => s?.vendorQuotations?.grandTotalWithoutGst,
      },
      {
        label: "Total GST:",
        getValue: (s) => {
          const withoutGst = s?.vendorQuotations?.grandTotalWithoutGst || 0;
          const withGst = s?.vendorQuotations?.grandTotalWithGst || 0;
          const gstAmount = withGst - withoutGst;
          return isNaN(gstAmount) ? 0 : gstAmount;
        },
      },
      {
        label: "Total Quotation Amount (Inc. GST):",
        getValue: (s) => s?.vendorQuotations?.grandTotalWithGst,
      },
    ];

    supplierDetailsLabels.forEach((detail, index) => {
      const rowIndex = summaryStartRowIndex + index;
      const row = sheet.getRow(rowIndex);
      sheet.mergeCells(`A${rowIndex}:I${rowIndex}`);
      row.getCell(1).value = detail.label;
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = 10 + supplierIndex * 8; // Changed from 6 to 8
        const value = detail.getValue(supplier);
        setNumericCell(row.getCell(colStart + 6), value); // Changed from +4 to +6
      });
    });

    // Add "Difference from Lowest Quote"
    const diffRowIndex = summaryStartRowIndex + supplierDetailsLabels.length;
    const diffRow = sheet.getRow(diffRowIndex);
    sheet.mergeCells(`A${diffRowIndex}:I${diffRowIndex}`);
    diffRow.getCell(1).value = "Difference from Lowest Quote:";
    diffRow.getCell(1).font = { bold: true };
    diffRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

    const l1Total =
      sortedSuppliers[0]?.vendorQuotations?.grandTotalWithoutGst ?? 0;

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 10 + supplierIndex * 8; // Changed from 6 to 8
      const grandTotal = supplier.vendorQuotations?.grandTotalWithoutGst ?? 0;

      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + 7).letter; // Changed from +5 to +7
      const mergeRange = `${startLetter}${diffRowIndex}:${endLetter}${diffRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${diffRowIndex}`);

      if (supplierIndex === 0) {
        cell.value = "Lowest Quote";
        cell.font = { italic: true };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else if (!supplier.quotationReceived) {
        // For vendors who didn't submit quotations
        cell.value = "Quotation not Submitted";
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else {
        const deviation = grandTotal - l1Total;
        const deviationPercent = l1Total
          ? ((deviation / l1Total) * 100).toFixed(2)
          : 0;
        cell.value = `Rs. ${new Intl.NumberFormat("en-IN").format(
          deviation
        )} (${deviationPercent}%)`;
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      }
    });

    const paymentTermsRowIndex = diffRowIndex + 1;
    const paymentRow = sheet.getRow(paymentTermsRowIndex);
    sheet.mergeCells(`A${paymentTermsRowIndex}:I${paymentTermsRowIndex}`);
    paymentRow.getCell(1).value = "Payment Terms:";
    paymentRow.getCell(1).font = { bold: true };
    paymentRow.getCell(1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };


    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 10 + supplierIndex * 8; // Changed from 6 to 8
      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + 7).letter; // Changed from +5 to +7
      const mergeRange = `${startLetter}${paymentTermsRowIndex}:${endLetter}${paymentTermsRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${paymentTermsRowIndex}`);
      const lastQuotationSet =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
      if (lastQuotationSet?.agreedToPaymentTerms) {
        cell.value = htmlToText(rfqData?.paymentTerms) || "-";
      } else {
        cell.value = htmlToText(lastQuotationSet?.counterPaymentTerms) || "-";
      }
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
    });

    const vendorNotesRowIndex = paymentTermsRowIndex + 1;
    const notesRow = sheet.getRow(vendorNotesRowIndex);
    sheet.mergeCells(`A${vendorNotesRowIndex}:I${vendorNotesRowIndex}`);
    notesRow.getCell(1).value = "Vendor Notes:";
    notesRow.getCell(1).font = { bold: true };
    notesRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 10 + supplierIndex * 8; // Changed from 6 to 8
      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + 7).letter; // Changed from +5 to +7
      const mergeRange = `${startLetter}${vendorNotesRowIndex}:${endLetter}${vendorNotesRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${vendorNotesRowIndex}`);
      cell.value =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes || "-";
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
    });

    const shortLowestNetRates = {};
    const toNumber = (value) => {
      if (value === null || value === undefined) return null;
    
      // If already a number, return it
      if (typeof value === "number") return value;
    
      // Remove ₹, commas, spaces, Rs., %
      const cleaned = String(value)
        .replace(/₹/g, "")
        .replace(/Rs\./gi, "")
        .replace(/,/g, "")
        .replace(/%/g, "")
        .trim();
    
      const num = Number(cleaned);
      return isNaN(num) ? null : num;
    };

    const setAmountCell = (cell, value) => {
      if (value === "-" || value === null || value === undefined || isNaN(value)) {
        cell.value = "-";
        cell.alignment = { horizontal: "right", vertical: "middle" };
        return;
      }
    
      cell.value = Number(value);   // THIS fixes string issue
      cell.numFmt = '#,##0.00';
      cell.alignment = { horizontal: "right", vertical: "middle" };
    };
    
    const thinBorder = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    
    const addVerticalSeparators = (
      sheet,
      {
        lastRow,
        staticEndCol = 5,      // Column E
        vendorStartCol = 6,    // Column F
        vendorColSpan = 3,     // 3 columns per vendor
        vendorCount,
      }
    ) => {
      const rows = sheet.getRows(1, lastRow);
    
      // 1️ Border after column E
      rows.forEach((row) => {
        const cell = row.getCell(staticEndCol);
        cell.border = {
          ...cell.border,
          right: { style: "thin" },
        };
      });
    
      // 2️ Border after each vendor block
      for (let i = 0; i < vendorCount; i++) {
        const endCol = vendorStartCol + i * vendorColSpan + (vendorColSpan - 1);
    
        rows.forEach((row) => {
          const cell = row.getCell(endCol);
          cell.border = {
            ...cell.border,
            right: { style: "thin" },
          };
        });
      }
    };
    
    

rfqData.materials.forEach((material) => {
  let minRate = Infinity;

  sortedSuppliers.forEach((supplier) => {
    if (!supplier.quotationReceived) return;

    const lastSet =
      supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

    const item = lastSet?.materialDetails?.find(
      (m) => m.id === material._id
    );

    if (!item || !item.basePrice) return;

    const netRate =
      item.basePrice - (item.basePrice * item.discount) / 100;

    if (netRate > 0 && netRate < minRate) {
      minRate = netRate;
    }
  });

  shortLowestNetRates[material._id] =
    minRate === Infinity ? null : minRate;
});

const shortSheet = workbook.addWorksheet("Material_Summary_View");

// Column widths
shortSheet.columns = [
  { width: 5 },   // A - Sr
  { width: 35 },  // B - Material
  { width: 10 },  // C - Qty
  { width: 10 },  // D - Unit
  { width: 15 },  // E - Lowest Rate NEW
  { width: 15 },  // F
  { width: 15 },  // G
  { width: 15 },  // H
];

// ---------------- RFQ INFO (ROWS 1–3) ----------------

// Row 1
shortSheet.mergeCells("A1:E1");
shortSheet.mergeCells("F1:H1");

shortSheet.getCell("A1").value = `Project Name: ${rfqData.projectName}`;
shortSheet.getCell("F1").value = `RFQ ID: ${rfqData.rfqId}`;

// Row 2
shortSheet.mergeCells("A2:E2");
shortSheet.mergeCells("F2:H2");

shortSheet.getCell("A2").value = `RFQ Name: ${rfqData?.name}`;
shortSheet.getCell("F2").value = `Created By: ${rfqData?.createdBy || "-"}`;

// Row 3
shortSheet.mergeCells("A3:E3");
shortSheet.mergeCells("F3:H3");

shortSheet.getCell("A3").value = `RFQ Type: ${rfqData.rfqType}`;
shortSheet.getCell("F3").value = `Created At: ${formatDate(rfqData.createdAt)}`;


// Styling
["A1", "A2", "A3", "F1", "F2", "F3"].forEach((cell) => {
  shortSheet.getCell(cell).font = { bold: true };
  shortSheet.getCell(cell).alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true,
  };
});


// ---------------- VENDOR HEADERS (ROW 4) ----------------

let shortStartCol = 6; // Column E

sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  shortSheet.mergeCells(`${startLetter}4:${endLetter}4`);

  const headerCell = shortSheet.getCell(`${startLetter}4`);
  headerCell.value =
    `${supplier.businessName}` +
    `(${supplier.vendorQuotations?.rank})`;

  headerCell.font = { bold: true };
  headerCell.alignment = {
    wrapText: true,
    vertical: "middle",
    horizontal: "center",
  };
});

shortSheet.getRow(4);

// Ensure equal width for vendor columns (Current / First / Amount)
sortedSuppliers.forEach((_, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;

  shortSheet.getColumn(colStart).width = 14;     // Current Rate
  shortSheet.getColumn(colStart + 1).width = 14; // First Rate
  shortSheet.getColumn(colStart + 2).width = 16; // Amount
});

/* ---------- BUDGET DEVIATION (ROW 5) ---------- */
if (typeof rfqData?.rfqBudget === "number") {
  const budgetRowIndex = 5;

  // Label A–E
  shortSheet.mergeCells(`A${budgetRowIndex}:E${budgetRowIndex}`);
  const labelCell = shortSheet.getCell(`A${budgetRowIndex}`);

  labelCell.value = `RFQ Budget: Rs. ${new Intl.NumberFormat("en-IN").format(
    rfqData.rfqBudget
  )}`;

  labelCell.font = { bold: true };
  labelCell.alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  // Vendor values (F onwards)
  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;
    const startLetter = shortSheet.getColumn(colStart).letter;
    const endLetter = shortSheet.getColumn(colStart + 2).letter;

    shortSheet.mergeCells(
      `${startLetter}${budgetRowIndex}:${endLetter}${budgetRowIndex}`
    );

    const cell = shortSheet.getCell(`${startLetter}${budgetRowIndex}`);

    if (!supplier.quotationReceived) {
      cell.value = "-";
      cell.alignment = { horizontal: "center", vertical: "middle" };
      return;
    }

    const rfqBudget = rfqData.rfqBudget;
    const totalWithGst =
      supplier?.vendorQuotations?.grandTotalWithGst ?? 0;
    const totalWithoutGst =
      supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

    const gst = totalWithGst - totalWithoutGst;

    const deviation = (rfqBudget - totalWithGst) + gst;
    const isOverBudget = deviation < 0;

    cell.value = `Rs. ${Math.abs(deviation).toLocaleString("en-IN")} ${
      isOverBudget ? "▲" : "▼"
    }`;

    cell.font = {
      bold: true,
      color: {
        argb: isOverBudget ? "FFFF0000" : "FF008000",
      },
    };

    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
  });
}

// ---------------- TABLE HEADERS (ROW 6) ----------------

const headerRow = shortSheet.getRow(6);
headerRow.height = 32;

// First 4 static headers
shortSheet.getRow(6).values = [
  "Sr.",
  "Material Name",
  "Qty",
  "Unit",
  "Lowest Rate", // NEW
];


[1, 2, 3, 4, 5].forEach((c) => {
  headerRow.getCell(c).font = { bold: true };
  headerRow.getCell(c).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD9D9D9" }, // light grey
  };
  headerRow.getCell(c).alignment = {
    horizontal: "center",
    vertical: "middle",
  };
});

// Vendor headers (E onwards)
sortedSuppliers.forEach((_, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;

  const setHeaderCell = (cell, text) => {
    cell.value = text;
    cell.font = { bold: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9D9D9" }, // light grey
    };
    cell.alignment = {
      wrapText: true,
      horizontal: "center",
      vertical: "middle",
    };
  };

  setHeaderCell(
    headerRow.getCell(colStart),
    "Quoted\nRate"
  );

  setHeaderCell(
    headerRow.getCell(colStart + 1),
    "Negotiation\nRate"
  );

  setHeaderCell(
    headerRow.getCell(colStart + 2),
    "Negotiated\nAmount"
  );
});


// ---------------- MATERIAL ROWS (START FROM ROW 6) ----------------
let currentRow = 7;

rfqData.materials.forEach((material, index) => {

  const hasAnyRemark = sortedSuppliers.some((supplier) => {
    if (!supplier.quotationReceived) return false;

    const lastSet =
      supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

    const item = lastSet?.materialDetails?.find(
      (m) => m.id === material._id
    );

    return !!item?.remark;
  });

  const baseRow = currentRow;
  const rateRow = shortSheet.getRow(baseRow);
  const remarkRow = hasAnyRemark
    ? shortSheet.getRow(baseRow + 1)
    : null;

  // -------- RATE ROW --------
  rateRow.getCell(1).value = index + 1;
  rateRow.getCell(2).value = material.name.replace(/<[^>]*>/g, "");
  rateRow.getCell(3).value = material.quantity;
  rateRow.getCell(4).value = getFormattedUnit(material.unit);
  rateRow.getCell(5).value = shortLowestNetRates[material._id] ?? "-";
  rateRow.getCell(5).alignment = { horizontal: "right", vertical: "middle" };

  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;

    if (!supplier.quotationReceived) {
      rateRow.getCell(colStart).value = "-";
      rateRow.getCell(colStart + 1).value = "-";
      rateRow.getCell(colStart + 2).value = "-";
      return;
    }

    const lastSet =
      supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
    const firstSet =
      supplier.vendorQuotations?.quotationSets?.[0];

    const lastItem = lastSet?.materialDetails?.find(
      (m) => m.id === material._id
    );
    const firstItem = firstSet?.materialDetails?.find(
      (m) => m.id === material._id
    );

    const netRate = lastItem
      ? lastItem.basePrice -
        (lastItem.basePrice * lastItem.discount) / 100
      : 0;

    const firstRate = firstItem
      ? firstItem.basePrice -
        (firstItem.basePrice * firstItem.discount) / 100
      : 0;

      // ----- First Rate (no highlight) -----
  setAmountCell(
    rateRow.getCell(colStart),
    firstRate
  );

  // ----- Current / Negotiation Rate (HIGHLIGHT LOWEST) -----
  const currentRateCell = rateRow.getCell(colStart + 1);
  const currentRate = Number(netRate);

  // 1️ Set value first
  setAmountCell(currentRateCell, currentRate);

  // 2️ Highlight lowest current rate
  const lowestRate = shortLowestNetRates[material._id];

  if (
    currentRate > 0 &&
    lowestRate != null &&
    Math.abs(currentRate - lowestRate) < 0.000001
  ) {
    currentRateCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFBBF7B3" }, // green
    };
  }


  // ----- Negotiated Amount -----
  setAmountCell(
    rateRow.getCell(colStart + 2),
    lastItem?.priceWithoutGst
  );
  });

  // -------- REMARK ROW --------
  if (hasAnyRemark) {
    [1, 2, 3, 4, 5].forEach((c) => {
      remarkRow.getCell(c).value = "";
    });
  
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      if (!supplier.quotationReceived) return;
  
      const colStart = shortStartCol + supplierIndex * 3;
      const lastSet =
        supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
  
      const item = lastSet?.materialDetails?.find(
        (m) => m.id === material._id
      );
  
      if (!item?.remark) return;
  
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(colStart + 2).letter;
  
      shortSheet.mergeCells(
        `${startLetter}${baseRow + 1}:${endLetter}${baseRow + 1}`
      );
  
      const cell = shortSheet.getCell(`${startLetter}${baseRow + 1}`);
      cell.value = item.remark.replace(/<[^>]*>/g, "");
      cell.font = { italic: true };
      cell.alignment = {
        wrapText: true,
        vertical: "top",
        horizontal: "left",
      };
  
      // HEIGHT FIX
      const lineCount = Math.ceil(cell.value.length / 50);
remarkRow.height = Math.max(18, lineCount * 15);

    });
  }

  currentRow += hasAnyRemark ? 2 : 1;
  
});

// Freeze RFQ + header rows
shortSheet.views = [{ state: "frozen", ySplit: 6 }];

// row 7
// ================= SUMMARY & FOOTER ROWS =================
const hasAnyPaymentTerms = sortedSuppliers.some((supplier) => {
  if (!supplier.quotationReceived) return false;

  const lastSet =
    supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

  return (
    !!rfqData?.paymentTerms ||
    !!lastSet?.counterPaymentTerms
  );
});

const hasAnyVendorNotes = sortedSuppliers.some((supplier) => {
  if (!supplier.quotationReceived) return false;

  return !!supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes;
});


let summaryStartRow = currentRow + 1;

  // material cost
  shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
  shortSheet.getCell(`A${summaryStartRow}`).value = "Material Cost :";
  shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
  shortSheet.getCell(`A${summaryStartRow}`).alignment = {
    horizontal: "right",
    vertical: "middle",
  };
  
  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;
  
    const startLetter = shortSheet.getColumn(colStart).letter;
    const endLetter = shortSheet.getColumn(colStart + 2).letter;
  
    shortSheet.mergeCells(
      `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
    );
  
    const cost =
      supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
        ?.totalWithoutGst ?? "-";
  
        setAmountCell(
          shortSheet.getCell(`${startLetter}${summaryStartRow}`),
          cost
        );
    shortSheet.getCell(`${startLetter}${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };
  });

  if (hasCommercialDiscount(rfqData, sortedSuppliers)) {
    summaryStartRow += 1;

    shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value =
      "Commercial Discount :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = shortStartCol + supplierIndex * 3;
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(colStart + 2).letter;
      const percent = getCommercialDiscountPercent(supplier);
      const amount = getCommercialDiscountAmount(supplier);

      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
      cell.value = amount > 0
        ? `Rs. ${new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 2,
          }).format(amount)}${percent > 0 ? ` (${percent}%)` : ""}`
        : "-";
      cell.alignment = { horizontal: "right", vertical: "middle" };
    });
  }
  
// ---------- OTHER CHARGES ----------

enabledCharges.forEach((charge) => {

  const hasAnyChargeValue = sortedSuppliers.some((supplier) => {
    if (!supplier.quotationReceived) return false;

    const value =
      supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
        ?.otherCharges?.[charge.key]?.mrp ?? 0;

    return Number(value) > 0;
  });

  //If all vendors have 0 → skip row
  if (!hasAnyChargeValue) return;

  summaryStartRow += 1;

  // ---- LABEL (A–E merged) ----
  shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
  shortSheet.getCell(`A${summaryStartRow}`).value = charge.label;
  shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
  shortSheet.getCell(`A${summaryStartRow}`).alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  // ---- VALUES (PER VENDOR, MERGED 3 COLUMNS) ----
  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;
    const startLetter = shortSheet.getColumn(colStart).letter;
    const endLetter = shortSheet.getColumn(colStart + 2).letter;

    shortSheet.mergeCells(
      `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
    );

    const chargeValue =
      supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
        ?.otherCharges?.[charge.key]?.mrp ?? 0;

    setAmountCell(
      shortSheet.getCell(`${startLetter}${summaryStartRow}`),
      chargeValue
    );
  });
});


// ---------- Total Basic Amount ----------
summaryStartRow += 1;

// Merge label A–E
shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
shortSheet.getCell(`A${summaryStartRow}`).value = "Total Basic Amount :";
shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
shortSheet.getCell(`A${summaryStartRow}`).alignment = {
  horizontal: "right",
  vertical: "middle",
};
shortSheet.getCell(`A${summaryStartRow}`).fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" }, // light grey
};

// Merge vendor columns (3 columns per vendor)
sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  shortSheet.mergeCells(
    `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
  );

  const value =
    supplier?.vendorQuotations?.grandTotalWithoutGst ?? "-";

  shortSheet.getCell(`${startLetter}${summaryStartRow}`).value = value;
  shortSheet.getCell(`${startLetter}${summaryStartRow}`).alignment = {
    horizontal: "right",
    vertical: "middle",
  };
  shortSheet.getCell(`${startLetter}${summaryStartRow}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD9D9D9" }, // light grey
  };
});


// ---------- Total GST ----------
summaryStartRow += 1;

// Merge label A–E
shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
shortSheet.getCell(`A${summaryStartRow}`).value = "Total GST :";
shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
shortSheet.getCell(`A${summaryStartRow}`).alignment = {
  horizontal: "right",
  vertical: "middle",
};

// Merge vendor columns (3 columns per vendor)
sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  shortSheet.mergeCells(
    `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
  );

  const withGst = supplier?.vendorQuotations?.grandTotalWithGst ?? 0;
  const withoutGst = supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;
  const gstAmount = withGst - withoutGst;

  shortSheet.getCell(`${startLetter}${summaryStartRow}`).value =
    isNaN(gstAmount) ? "-" : gstAmount;

  shortSheet.getCell(`${startLetter}${summaryStartRow}`).alignment = {
    horizontal: "right",
    vertical: "middle",
  };
});


// ---------- Total Quotation Amount (Incl. GST) ----------
summaryStartRow += 1;

// Merge label A–E
shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
// Set value
shortSheet.getCell(`A${summaryStartRow}`).value =
  "Total Quotation Value (Incl. GST) :";

// Apply grey background to the entire merged row (A–E)
["A", "B", "C", "D", "E"].forEach((col) => {
  const cell = shortSheet.getCell(`${col}${summaryStartRow}`);
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD9D9D9" }, // light grey
  };
  
});

shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
shortSheet.getCell(`A${summaryStartRow}`).alignment = {
  horizontal: "right",
  vertical: "middle",
};

// Merge vendor columns (3 columns per vendor)
sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  shortSheet.mergeCells(
    `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
  );

  const totalWithGst =
    supplier?.vendorQuotations?.grandTotalWithGst ?? "-";

  shortSheet.getCell(`${startLetter}${summaryStartRow}`).value =
    totalWithGst;

  shortSheet.getCell(`${startLetter}${summaryStartRow}`).alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  shortSheet.getCell(`${startLetter}${summaryStartRow}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD9D9D9" }, // light grey
  };
});

// ---------- Difference from Lowest Quote ----------
summaryStartRow += 1;

// Merge label A–E
shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
shortSheet.getCell(`A${summaryStartRow}`).value =
  "Difference from Lowest Quote :";
shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
shortSheet.getCell(`A${summaryStartRow}`).alignment = {
  horizontal: "right",
  vertical: "middle",
};

// L1 reference (lowest total without GST)
const shortL1Total =
  sortedSuppliers[0]?.vendorQuotations?.grandTotalWithoutGst ?? 0;


// Merge vendor columns (3 columns per vendor)
sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  shortSheet.mergeCells(
    `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
  );

  const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);

  if (supplierIndex === 0) {
    cell.value = "Lowest Quote";
    cell.font = { italic: true };
  } else if (!supplier.quotationReceived) {
    cell.value = "Quotation Not Submitted";
  } else {
    const currentTotal =
      supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

      const diff = currentTotal - shortL1Total;

      setAmountCell(cell, diff);
    }

  cell.alignment = {
    horizontal: "right",
    vertical: "middle",
    wrapText: true,
  };
});

// ---------- Payment Terms ----------
if (hasAnyPaymentTerms) {
  summaryStartRow += 1;

  shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
  shortSheet.getCell(`A${summaryStartRow}`).value = "Payment Terms :";
  shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
  shortSheet.getCell(`A${summaryStartRow}`).alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;
    const startLetter = shortSheet.getColumn(colStart).letter;
    const endLetter = shortSheet.getColumn(colStart + 2).letter;

    shortSheet.mergeCells(
      `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
    );

    const lastSet =
      supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

    const text =
      lastSet?.agreedToPaymentTerms
        ? htmlToText(rfqData?.paymentTerms)
        : htmlToText(lastSet?.counterPaymentTerms) || "-";

    const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
    cell.value = text;
    cell.alignment = {
      horizontal: "left",
      vertical: "top",
      wrapText: true,
    };

    // AUTO HEIGHT
    applyAutoRowHeight(shortSheet, summaryStartRow, text, {
      mergedCols: 3,
      columnWidth: 15,
    });
  });
}


// ---------- Vendor Notes ----------
if (hasAnyVendorNotes) {
  summaryStartRow += 1;

  shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
  shortSheet.getCell(`A${summaryStartRow}`).value = "Vendor Notes :";
  shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
  shortSheet.getCell(`A${summaryStartRow}`).alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;
    const startLetter = shortSheet.getColumn(colStart).letter;
    const endLetter = shortSheet.getColumn(colStart + 2).letter;

    shortSheet.mergeCells(
      `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
    );

    const text =
      supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes || "-";

    const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
    cell.value = text;
    cell.alignment = {
      horizontal: "left",
      vertical: "top",
      wrapText: true,
    };

    // AUTO HEIGHT
    applyAutoRowHeight(shortSheet, summaryStartRow, text, {
      mergedCols: 3,
      columnWidth: 15,
    });
  });
}

addVerticalSeparators(shortSheet, {
  lastRow: summaryStartRow,
  staticEndCol: 5,              // Column E
  vendorStartCol: shortStartCol, // Column F
  vendorColSpan: 3,             // Each vendor = 3 columns
  vendorCount: sortedSuppliers.length,
});

// FORCE border till Vendor Notes row
applyOuterBorderToRange(shortSheet, summaryStartRow);


    const buffer = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buffer]));
    const link = document.createElement("a");
    link.href = url;
    const fileName = `${rfqData?.name?.substring(0, 25) || "Export"}_${rfqData?.rfqType || ""}_${rfqData?.rfqId || ""}.xlsx`;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);

    enqueueSnackbar("Excel downloaded successfully!", {
      variant: "success",
      autoHideDuration: 3000,
    });
  } catch (error) {
    console.error("Error generating material comparison Excel:", error);
    enqueueSnackbar("Failed to download Excel file.", {
      variant: "error",
      autoHideDuration: 3000,
    });
  } finally {
    setExcelLoading(false);
    setLoadingMessage("");
  }
};

export const handleDownloadLabourComparisonExcel = async (
  rfqData,
  setExcelLoading,
  setLoadingMessage,
  enqueueSnackbar
) => {
  // Include ALL suppliers (both with and without quotations)
  const allSuppliers = rfqData?.suppliers || [];

  const calculatePercentageDifference = (firstPrice, latestPrice) => {
    if (!firstPrice || firstPrice === 0) return 0;
    return latestPrice - firstPrice;
  };

  // Separate suppliers into two groups: those with quotations and those without
  const suppliersWithQuotations = allSuppliers.filter(
    (supplier) => supplier?.quotationReceived
  );
  const suppliersWithoutQuotations = allSuppliers.filter(
    (supplier) => !supplier?.quotationReceived
  );

  // Sort only the suppliers with quotations by rank
  // const getRankNumber = (rank) => {
  //   if (!rank || rank === "NA") return Infinity; // push to last
  //   const num = parseInt(rank.replace(/\D/g, ""), 10);
  //   return isNaN(num) ? Infinity : num;
  // };
  
  const sortedSuppliersWithQuotes = [...suppliersWithQuotations].sort(
    (a, b) => {
      const rankA = getRankNumber(a.vendorQuotations?.rank);
      const rankB = getRankNumber(b.vendorQuotations?.rank);
      return rankA - rankB;
    }
  );

  // Combine sorted suppliers with quotes and unsorted suppliers without quotes
  const sortedSuppliers = [
    ...sortedSuppliersWithQuotes,
    ...suppliersWithoutQuotations,
  ];

  const awardedSuppliersAmount = rfqData?.suppliers
    ?.filter((supplier) => supplier.isAwarded)
    .reduce((total, supplier) => {
      const amount = supplier?.closingDetails?.[0]?.amount || 0;
      return total + amount;
    }, 0);

  const diff = rfqData?.rfqBudget - awardedSuppliersAmount;
  const isOverBudget = diff < 0;
  const formattedDiff = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
  }).format(Math.abs(diff));
  const budgetDeviationText = `Budget Deviation: Rs. ${formattedDiff || "NA"} ${
    isOverBudget ? "▲" : "▼"
  }`;

  try {
    setExcelLoading(true);
    setLoadingMessage("Generating Labour Comparison Excel...");

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Labour Comparison");

    const setNumericCell = (cell, value) => {
      cell.value = value ?? 0;
      cell.numFmt = "#,##0.00";
      cell.alignment = { horizontal: "right", vertical: "middle" };
    };

    sheet.columns = [
      { header: "Sr.", key: "sr", width: 5 },
      { header: "Work Description", key: "workDescription", width: 40 },
      { header: "Quantity", key: "quantity", width: 10 },
      { header: "Unit", key: "unit", width: 10 },
    ];

    // Update RFQ header to show both responding and non-responding vendors
    const rfqHeaderRange = "A1:D1";
    sheet.mergeCells(rfqHeaderRange);
    let budgetLine = "";

    if (hasValidBudget(rfqData)) {
      budgetLine = `RFQ Budget: Rs. ${new Intl.NumberFormat("en-IN").format(
        rfqData.rfqBudget
      )}\n`;
    }

    sheet.getCell("A1").value =
      `Project Name: ${rfqData.projectName}\n` +
      `Project Location: ${rfqData.location}\n` +
      `Neevay RFQ ID: ${rfqData.rfqId}\n` +
      `RFQ Date: ${new Date(rfqData.createdAt).toLocaleDateString("en-GB")}\n` +
      `Neevay RFQ Subject: ${rfqData.name}\n` +
      `Total Quotations: ${suppliersWithQuotations.length}\n` +
      `Total Invited Vendors: ${allSuppliers.length}\n` + // Added line to show total invited vendors
      budgetLine;
    sheet.getCell("A1").alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true,
    };
    sheet.getCell("A1").font = { bold: true };

    sheet.getRow(2).values = ["Sr.", "Work Description", "Quantity", "Unit"];
    sheet.getRow(2).font = { bold: true };
    sheet.getRow(2).alignment = { vertical: "middle", horizontal: "center" };

    // Find lowest rates only from suppliers who submitted quotations
    const lowestRates = {};
    rfqData?.labourDetails?.forEach((labour) => {
      let minRate = Infinity;
      let minSupplierIndexes = [];

      sortedSuppliersWithQuotes?.forEach((supplier, vendorIndex) => {
        const vendorQuotation =
          supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
        const vendorItem = vendorQuotation?.labourItems?.find(
          (item) => item.id === labour._id
        );

        if (vendorItem && vendorItem.labourRate > 0) {
          if (vendorItem.labourRate < minRate) {
            minRate = vendorItem.labourRate;
            minSupplierIndexes = [vendorIndex];
          } else if (vendorItem.labourRate === minRate) {
            minSupplierIndexes.push(vendorIndex);
          }
        }
      });

      lowestRates[labour._id] = {
        minRate: minRate === Infinity ? 0 : minRate,
        minSupplierIndexes,
      };
    });

    // Populate labour details
    rfqData.labourDetails.forEach((labour, index) => {
      const row = sheet.getRow(index + 3);
      row.getCell(1).value = index + 1;
      row.getCell(2).value = labour.skill.replace(/<[^>]*>/g, "");
      setNumericCell(row.getCell(3), Number(labour.quantity));
      row.getCell(4).value = getFormattedUnit(labour.unit);
    });

    // Add supplier columns
    let startCol = 5; // Column E
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = startCol + supplierIndex * 6;
      const colEnd = colStart + 5;
      const colLetter = sheet.getColumn(colStart).letter;
      const supplierHeaderRange = `${colLetter}1:${
        sheet.getColumn(colEnd).letter
      }1`;
      sheet.mergeCells(supplierHeaderRange);

      let budgetDeviationLine = "";

      if (
        hasValidBudget(rfqData) &&
        supplier.quotationReceived &&
        typeof supplier?.vendorQuotations?.grandTotalWithoutGst === "number"
      ) {
        const deviation =
          rfqData.rfqBudget -
          supplier.vendorQuotations.grandTotalWithoutGst;

        budgetDeviationLine =
          `Budget Deviation: Rs. ${new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(Math.abs(deviation))} ${
            deviation < 0 ? "▲" : "▼"
          }\n\n`;
      }

      // Different header for suppliers without quotations
      const supplierDetails = supplier.quotationReceived
        ? `Vendor Rank: ${supplier.vendorQuotations?.rank || "N/A"}\n` +
          `Vendor Name: ${supplier.businessName}\n` +
          `Vendor Phone: ${supplier.contactDetails[0]?.mobile}\n` +
          `Vendor Email: ${supplier.contactDetails[0]?.email}\n` +
          budgetDeviationLine
        : `Vendor Name: ${supplier.businessName}\n` +
          `Vendor Phone: ${supplier.contactDetails[0]?.mobile}\n` +
          `Vendor Email: ${supplier.contactDetails[0]?.email}\n` +
          `Status: Did Not Submit Quotation\n\n`;

      sheet.getCell(`${colLetter}1`).value = supplierDetails;
      sheet.getCell(`${colLetter}1`).alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
      };
      sheet.getCell(`${colLetter}1`).font = { bold: true };

      // Set column headers
      sheet.getRow(2).getCell(colStart).value = "Current Rate";
      sheet.getRow(2).getCell(colStart + 1).value = "First Rate";
      sheet.getRow(2).getCell(colStart + 2).value = "Difference";
      sheet.getRow(2).getCell(colStart + 3).value = "GST";
      sheet.getRow(2).getCell(colStart + 4).value = "Basic Amount";
      sheet.getRow(2).getCell(colStart + 5).value = "Vendor Remarks";

      // Set column widths
      [12, 12, 12, 8, 15, 35].forEach(
        (w, i) => (sheet.getColumn(colStart + i).width = w)
      );

      // Populate labour rates for each supplier
      rfqData.labourDetails.forEach((labour, index) => {
        const row = sheet.getRow(index + 3);

        if (supplier.quotationReceived) {
          const lastQuotationSet =
            supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
          const firstQuotationSet =
            supplier.vendorQuotations?.quotationSets?.[0];
          const quotedLabour = lastQuotationSet?.labourItems?.find(
            (l) => l.id === labour._id
          );
          const firstQuotedLabour = firstQuotationSet?.labourItems?.find(
            (l) => l.id === labour._id
          );

          if (quotedLabour) {
            // Current Rate
            setNumericCell(row.getCell(colStart), quotedLabour.labourRate);

            // First Rate
            const firstRate = firstQuotedLabour?.labourRate || 0;
            setNumericCell(
              row.getCell(colStart + 1),
              firstRate > 0 ? firstRate : "-"
            );

            // Negotiation (difference between first and current rate)
            const percentageDifference = calculatePercentageDifference(
              firstRate,
              quotedLabour.labourRate
            );
            const displayDifference = Math.abs(percentageDifference);
            const negotiationCell = row.getCell(colStart + 2);
            setNumericCell(negotiationCell, displayDifference);

            // Style negotiation cell
            if (percentageDifference < 0) {
              negotiationCell.font = { color: { argb: "FF00AA00" } }; // Green
            } else if (percentageDifference > 0) {
              negotiationCell.font = { color: { argb: "FFFF0000" } }; // Red
            }

            // GST
            const gst = parseFloat(quotedLabour.gst) || 0;
            row.getCell(colStart + 3).value = gst / 100;
            row.getCell(colStart + 3).numFmt = "0.00%";
            row.getCell(colStart + 3).alignment = { horizontal: "right" };

            // Basic Amount
            setNumericCell(
              row.getCell(colStart + 4),
              quotedLabour.priceWithoutGst
            );

            // Vendor Remarks
            row.getCell(colStart + 5).value = quotedLabour.remarks.replace(
              /<[^>]*>/g,
              ""
            );
            row.getCell(colStart + 5).alignment = { wrapText: true };

            // Highlight lowest rate
            const isLowest =
              lowestRates[labour._id]?.minSupplierIndexes.includes(
                supplierIndex
              );
            if (isLowest) {
              row.getCell(colStart).style = {
                fill: {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFBBF7B3" }, // Light green
                },
              };
            }
          }
        } else {
          // For vendors who didn't submit quotations, leave all cells empty with "-"
          for (let i = 0; i < 6; i++) {
            row.getCell(colStart + i).value = "-";
            row.getCell(colStart + i).alignment = { horizontal: "center" };
          }
        }
      });

      const lineCount = supplierDetails.split("\n").length;
      sheet.getRow(1).height = Math.max(20, lineCount * 22);
    });

    const labourCostRowIndex = rfqData.labourDetails.length + 3 + 1;
    const labourCostRow = sheet.getRow(labourCostRowIndex);
    sheet.mergeCells(`A${labourCostRowIndex}:D${labourCostRowIndex}`);
    labourCostRow.getCell(1).value = "Labour Cost:";
    labourCostRow.getCell(1).font = { bold: true };
    labourCostRow.getCell(1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 5 + supplierIndex * 6;
      const cost =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
          ?.totalWithoutGst ?? 0;
      setNumericCell(labourCostRow.getCell(colStart + 4), cost);
    });

    // Add "Other Charges" rows
    const chargeLabels = [
      { label: "Transport Charges", key: "transport" },
      { label: "Mathade Charges", key: "mathadeCharges" },
      { label: "Loading Charges", key: "loading" },
      { label: "Unloading Charges", key: "unLoading" },
      { label: "Miscellaneous Charges", key: "miscCharges" },
    ];
    const enabledCharges = chargeLabels.filter(
      (charge) => rfqData?.otherCharges?.[charge.key]
    );

    enabledCharges.forEach((charge, index) => {
      const rowIndex = labourCostRowIndex + index + 1;
      const row = sheet.getRow(rowIndex);
      sheet.mergeCells(`A${rowIndex}:D${rowIndex}`);
      row.getCell(1).value = charge.label;
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = 5 + supplierIndex * 6;
        const chargeMRP =
          supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
            ?.otherCharges?.[charge.key]?.mrp ?? 0;
        setNumericCell(row.getCell(colStart + 4), chargeMRP);
      });
    });

    // Add Summary Rows (Basic Amount, GST, Total, Payment Terms)
    const summaryStartRowIndex = labourCostRowIndex + enabledCharges.length + 1;
    const supplierDetailsLabels = [
      {
        label: "Total Basic Amount:",
        getValue: (s) => s?.vendorQuotations?.grandTotalWithoutGst,
      },
      {
        label: "Total GST:",
        getValue: (s) => {
          const withoutGst = s?.vendorQuotations?.grandTotalWithoutGst || 0;
          const withGst = s?.vendorQuotations?.grandTotalWithGst || 0;
          const gstAmount = withGst - withoutGst;
          return isNaN(gstAmount) ? 0 : gstAmount;
        },
      },
      {
        label: "Total Quotation Amount (Inc. GST):",
        getValue: (s) => s?.vendorQuotations?.grandTotalWithGst,
      },
    ];

    supplierDetailsLabels.forEach((detail, index) => {
      const rowIndex = summaryStartRowIndex + index;
      const row = sheet.getRow(rowIndex);
      sheet.mergeCells(`A${rowIndex}:D${rowIndex}`);
      row.getCell(1).value = detail.label;
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = 5 + supplierIndex * 6;
        const value = detail.getValue(supplier);
        setNumericCell(row.getCell(colStart + 4), value);
      });
    });

    // Add "Difference from Lowest Quote"
    const diffRowIndex = summaryStartRowIndex + supplierDetailsLabels.length;
    const diffRow = sheet.getRow(diffRowIndex);
    sheet.mergeCells(`A${diffRowIndex}:D${diffRowIndex}`);
    diffRow.getCell(1).value = "Difference from Lowest Quote:";
    diffRow.getCell(1).font = { bold: true };
    diffRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

    const l1Total =
      sortedSuppliers[0]?.vendorQuotations?.grandTotalWithoutGst ?? 0;

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 5 + supplierIndex * 6;
      const grandTotal = supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + 5).letter;
      const mergeRange = `${startLetter}${diffRowIndex}:${endLetter}${diffRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${diffRowIndex}`);

      if (supplierIndex === 0) {
        cell.value = "Lowest Quote";
        cell.font = { italic: true };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else if (!supplier.quotationReceived) {
        cell.value = "No Quote Submitted";
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else {
        const deviation = grandTotal - l1Total;
        const deviationPercent = l1Total
          ? ((deviation / l1Total) * 100).toFixed(2)
          : 0;
        cell.value = `Rs. ${new Intl.NumberFormat("en-IN").format(
          deviation
        )} (${deviationPercent}%)`;
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      }
    });

    // Add "Payment Terms" row
    const paymentTermsRowIndex = diffRowIndex + 1;
    const paymentRow = sheet.getRow(paymentTermsRowIndex);
    sheet.mergeCells(`A${paymentTermsRowIndex}:D${paymentTermsRowIndex}`);
    paymentRow.getCell(1).value = "Payment Terms:";
    paymentRow.getCell(1).font = { bold: true };
    paymentRow.getCell(1).alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 5 + supplierIndex * 6;
      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + 5).letter;
      const mergeRange = `${startLetter}${paymentTermsRowIndex}:${endLetter}${paymentTermsRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${paymentTermsRowIndex}`);
      const lastQuotationSet =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
      if (lastQuotationSet?.agreedToPaymentTerms) {
        cell.value = htmlToText(rfqData?.paymentTerms) || "-";
      } else {
        cell.value = htmlToText(lastQuotationSet?.counterPaymentTerms) || "-";
      }
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
    });

    // Add "Vendor Notes" row
    const vendorNotesRowIndex = paymentTermsRowIndex + 1;
    const notesRow = sheet.getRow(vendorNotesRowIndex);
    sheet.mergeCells(`A${vendorNotesRowIndex}:D${vendorNotesRowIndex}`);
    notesRow.getCell(1).value = "Vendor Notes:";
    notesRow.getCell(1).font = { bold: true };
    notesRow.getCell(1).alignment = { horizontal: "left", vertical: "middle" };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 5 + supplierIndex * 6;
      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + 5).letter;
      const mergeRange = `${startLetter}${vendorNotesRowIndex}:${endLetter}${vendorNotesRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${vendorNotesRowIndex}`);
      cell.value =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes || "-";
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
    });

/* ================= LABOUR SUMMARY VIEW ================= */

const shortLowestLabourRates = {};

const setAmountCell = (cell, value) => {
  if (value === "-" || value === null || value === undefined || isNaN(value)) {
    cell.value = "-";
    cell.alignment = { horizontal: "right", vertical: "middle" };
    return;
  }

  cell.value = Number(value);   // THIS fixes string issue
  cell.numFmt = '#,##0.00';
  cell.alignment = { horizontal: "right", vertical: "middle" };
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB"); // Formats as DD/MM/YYYY
};

const addVerticalSeparators = (
  sheet,
  {
    lastRow,
    staticEndCol = 5,      // Column E
    vendorStartCol = 6,    // Column F
    vendorColSpan = 3,     // 3 columns per vendor
    vendorCount,
  }
) => {
  const rows = sheet.getRows(1, lastRow);

  // 1️ Border after column E
  rows.forEach((row) => {
    const cell = row.getCell(staticEndCol);
    cell.border = {
      ...cell.border,
      right: { style: "thin" },
    };
  });

  // 2️ Border after each vendor block
  for (let i = 0; i < vendorCount; i++) {
    const endCol = vendorStartCol + i * vendorColSpan + (vendorColSpan - 1);

    rows.forEach((row) => {
      const cell = row.getCell(endCol);
      cell.border = {
        ...cell.border,
        right: { style: "thin" },
      };
    });
  }
};

rfqData.labourDetails.forEach((labour) => {
  let minRate = Infinity;

  sortedSuppliers.forEach((supplier) => {
    if (!supplier.quotationReceived) return;

    const lastSet =
      supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

    const item = lastSet?.labourItems?.find(
      (l) => l.id === labour._id
    );

    if (!item || !item.labourRate) return;

    if (item.labourRate > 0 && item.labourRate < minRate) {
      minRate = item.labourRate;
    }
  });

  shortLowestLabourRates[labour._id] =
    minRate === Infinity ? null : minRate;
});

// ---------- CREATE SUMMARY SHEET ----------
const shortSheet = workbook.addWorksheet("Labour_Summary_View");

const TOTAL_VENDOR_COLS = sortedSuppliers.length * 3;

// ---------- COLUMN WIDTHS ----------
shortSheet.columns = [
  { width: 5 },   // Sr
  { width: 40 },  // Work Description
  { width: 10 },  // Qty
  { width: 10 },  // Unit
  { width: 15 },  // Lowest Rate
  ...Array(TOTAL_VENDOR_COLS).fill({ width: 15 }),
];

// ---------- RFQ HEADER ----------
shortSheet.mergeCells("A1:E1");
shortSheet.mergeCells("F1:H1");
shortSheet.mergeCells("A2:E2");
shortSheet.mergeCells("F2:H2");
shortSheet.mergeCells("A3:E3");
shortSheet.mergeCells("F3:H3");

shortSheet.getCell("A1").value = `Project Name: ${rfqData.projectName}`;
shortSheet.getCell("F1").value = `RFQ ID: ${rfqData.rfqId}`;
shortSheet.getCell("A2").value = `RFQ Name: ${rfqData.name}`;
shortSheet.getCell("F2").value = `Created By: ${rfqData.createdBy || "-"}`;
shortSheet.getCell("A3").value = `RFQ Type: labour`;
shortSheet.getCell("F3").value = `Created At: ${formatDate(rfqData.createdAt)}`;

["A1","A2","A3","F1","F2","F3"].forEach((cell) => {
  shortSheet.getCell(cell).font = { bold: true };
  shortSheet.getCell(cell).alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true,
  };
});

// ---------- VENDOR HEADERS ----------
let shortStartCol = 6;

sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  shortSheet.mergeCells(`${startLetter}4:${endLetter}4`);

  shortSheet.getCell(`${startLetter}4`).value =
    `${supplier.businessName} (${supplier.vendorQuotations?.rank || "-"})`;

  shortSheet.getCell(`${startLetter}4`).font = { bold: true };
  shortSheet.getCell(`${startLetter}4`).alignment = {
    wrapText: true,
    vertical: "middle",
    horizontal: "center",
  };
});

/* ---------- BUDGET DEVIATION (ROW 5) ---------- */
if (hasValidBudget(rfqData)) {
  const budgetRowIndex = 5;

  // Label A–E
  shortSheet.mergeCells(`A${budgetRowIndex}:E${budgetRowIndex}`);
  const labelCell = shortSheet.getCell(`A${budgetRowIndex}`);

  labelCell.value = `RFQ Budget: Rs. ${new Intl.NumberFormat("en-IN").format(
    rfqData.rfqBudget
  )}`;

  labelCell.font = { bold: true };
  labelCell.alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  // Vendor values (F onwards)
  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;
    const startLetter = shortSheet.getColumn(colStart).letter;
    const endLetter = shortSheet.getColumn(colStart + 2).letter;

    shortSheet.mergeCells(
      `${startLetter}${budgetRowIndex}:${endLetter}${budgetRowIndex}`
    );

    const cell = shortSheet.getCell(`${startLetter}${budgetRowIndex}`);

    if (!supplier.quotationReceived) {
      cell.value = "-";
      cell.alignment = { horizontal: "center", vertical: "middle" };
      return;
    }

    const rfqBudget = rfqData.rfqBudget;
    const totalWithGst = supplier.vendorQuotations.grandTotalWithGst;
    const totalWithoutGst = supplier.vendorQuotations.grandTotalWithoutGst;
    const totalGst = totalWithGst - totalWithoutGst;
    
    const deviation = (rfqBudget - totalWithGst) + totalGst;    
    const isOverBudget = deviation < 0;

    cell.value = `Rs. ${Math.abs(deviation).toLocaleString("en-IN")} ${
      isOverBudget ? "▲" : "▼"
    }`;

    cell.font = {
      bold: true,
      color: {
        argb: isOverBudget ? "FFFF0000" : "FF008000",
      },
    };

    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
  });
}


// ---------- TABLE HEADERS ----------
const headerRowIndex = hasValidBudget(rfqData) ? 6 : 5;
const headerRow = shortSheet.getRow(headerRowIndex);

headerRow.height = 32;

headerRow.values = [
  "Sr.",
  "Work Description",
  "Qty",
  "Unit",
  "Lowest Rate",
];

[1,2,3,4,5].forEach((c) => {
  headerRow.getCell(c).font = { bold: true };
  headerRow.getCell(c).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD9D9D9" },
  };
  headerRow.getCell(c).alignment = {
    horizontal: "center",
    vertical: "middle",
  };
});

// Vendor sub headers
sortedSuppliers.forEach((_, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;

  const setHeader = (cell, text) => {
    cell.value = text;
    cell.font = { bold: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9D9D9" },
    };
    cell.alignment = {
      wrapText: true,
      horizontal: "center",
      vertical: "middle",
    };
  };

  setHeader(headerRow.getCell(colStart), "Quoted\nRate");
  setHeader(headerRow.getCell(colStart + 1), "Negotiation\nRate");
  setHeader(headerRow.getCell(colStart + 2), "Negotiated\nAmount");
});

// ---------- LABOUR ROWS ----------
let currentRow = headerRowIndex + 1;

rfqData.labourDetails.forEach((labour, index) => {

  // 1️ Check if ANY vendor has remark for this labour item
  const hasAnyRemark = sortedSuppliers.some((supplier) => {
    if (!supplier.quotationReceived) return false;

    const lastSet =
      supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

    const item = lastSet?.labourItems?.find(
      (l) => l.id === labour._id
    );

    return !!item?.remarks;
  });

  // 2️ Define rows INSIDE loop (scope fix)
  const baseRow = currentRow;
  const row = shortSheet.getRow(baseRow);
  const remarkRow = hasAnyRemark
    ? shortSheet.getRow(baseRow + 1)
    : null;

  // const row = shortSheet.getRow(currentRow);

  row.getCell(1).value = index + 1;
  row.getCell(2).value = labour.skill.replace(/<[^>]*>/g, "");
  row.getCell(3).value = labour.quantity;
  row.getCell(4).value = getFormattedUnit(labour.unit);
  row.getCell(5).value = shortLowestLabourRates[labour._id] ?? "-";

  row.getCell(5).alignment = { horizontal: "right" };

  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;

    if (!supplier.quotationReceived) {
      row.getCell(colStart).value = "-";
      row.getCell(colStart + 1).value = "-";
      row.getCell(colStart + 2).value = "-";
      return;
    }

    const lastSet =
      supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
    const firstSet =
      supplier.vendorQuotations?.quotationSets?.[0];

    const lastItem = lastSet?.labourItems?.find(
      (l) => l.id === labour._id
    );
    const firstItem = firstSet?.labourItems?.find(
      (l) => l.id === labour._id
    );

    // ----- Quoted Rate (no highlight) -----
  setAmountCell(
    row.getCell(colStart),
    firstItem?.labourRate ?? 0
  );

  // ----- Negotiation Rate (HIGHLIGHT LOWEST) -----
  const negotiationCell = row.getCell(colStart + 1);
  const negotiationRate = Number(lastItem?.labourRate ?? 0);

  // 1️ Set value first
  setAmountCell(negotiationCell, negotiationRate);

  // 2️ Highlight lowest negotiation rate
  if (
    negotiationRate > 0 &&
    shortLowestLabourRates[labour._id] === negotiationRate
  ) {
    negotiationCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFBBF7B3" }, // SAME GREEN AS MATERIAL
    };
  }

  // ----- Negotiated Amount -----
  setAmountCell(
    row.getCell(colStart + 2),
    lastItem?.priceWithoutGst ?? 0
  );

  });

  if (hasAnyRemark) {
    // Clear static columns
    [1, 2, 3, 4, 5].forEach((c) => {
      remarkRow.getCell(c).value = "";
    });

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      if (!supplier.quotationReceived) return;

      const colStart = shortStartCol + supplierIndex * 3;
      const lastSet =
        supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

      const item = lastSet?.labourItems?.find(
        (l) => l.id === labour._id
      );

      if (!item?.remarks) return;

      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(colStart + 2).letter;

      shortSheet.mergeCells(
        `${startLetter}${baseRow + 1}:${endLetter}${baseRow + 1}`
      );
      const cell = shortSheet.getCell(`${startLetter}${baseRow + 1}`);


      const remarkText = item.remarks.replace(/<[^>]*>/g, "");
      cell.value = remarkText;
      cell.alignment = {
        wrapText: true,
        vertical: "top",
        horizontal: "left",
      };
  
      // ADD THIS LINE HERE (ONLY THIS)
      applyAutoRowHeight(shortSheet, baseRow + 1, remarkText, {
        mergedCols: 3,
        columnWidth: 15,
      });
    });
  }


  currentRow += hasAnyRemark ? 2 : 1;

});

// ---------- FREEZE HEADERS ----------
shortSheet.views = [
  {
    state: "frozen",
    ySplit: hasValidBudget(rfqData) ? 5 : 4,
  },
];


/* ================= LABOUR SUMMARY TOTALS ================= */

let summaryStartRow = currentRow + 1;

/* ---------- LABOUR COST ---------- */
shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
shortSheet.getCell(`A${summaryStartRow}`).value = "Labour Cost :";
shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
shortSheet.getCell(`A${summaryStartRow}`).alignment = {
  horizontal: "right",
  vertical: "middle",
};

sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  shortSheet.mergeCells(
    `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
  );

  const cost =
    supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
      ?.totalWithoutGst ?? "-";

  setAmountCell(shortSheet.getCell(`${startLetter}${summaryStartRow}`), cost);
});

chargeLabels.forEach((charge) => {
  const hasAnyCharge = sortedSuppliers.some((supplier) => {
    if (!supplier.quotationReceived) return false;
    return (
      supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
        ?.otherCharges?.[charge.key]?.mrp > 0
    );
  });

  if (!hasAnyCharge) return;

  summaryStartRow += 1;

  shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
  shortSheet.getCell(`A${summaryStartRow}`).value = charge.label;
  shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
  shortSheet.getCell(`A${summaryStartRow}`).alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;
    const startLetter = shortSheet.getColumn(colStart).letter;
    const endLetter = shortSheet.getColumn(colStart + 2).letter;

    shortSheet.mergeCells(
      `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
    );

    const chargeValue =
      supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
        ?.otherCharges?.[charge.key]?.mrp ?? 0;

    setAmountCell(
      shortSheet.getCell(`${startLetter}${summaryStartRow}`),
      chargeValue
    );
  });
});

/* ---------- TOTAL BASIC AMOUNT ---------- */
summaryStartRow += 1;

shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
shortSheet.getCell(`A${summaryStartRow}`).value = "Total Basic Amount :";
shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
shortSheet.getCell(`A${summaryStartRow}`).alignment = {
  horizontal: "right",
  vertical: "middle",
};
shortSheet.getCell(`A${summaryStartRow}`).fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  shortSheet.mergeCells(
    `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
  );

  setAmountCell(
    shortSheet.getCell(`${startLetter}${summaryStartRow}`),
    supplier?.vendorQuotations?.grandTotalWithoutGst ?? "-"
  );
  shortSheet.getCell(`${startLetter}${summaryStartRow}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD9D9D9" }, // light grey
  };
});

/* ---------- TOTAL GST ---------- */
summaryStartRow += 1;

shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
shortSheet.getCell(`A${summaryStartRow}`).value = "Total GST :";
shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
shortSheet.getCell(`A${summaryStartRow}`).alignment = {
  horizontal: "right",
  vertical: "middle",
};

sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  shortSheet.mergeCells(
    `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
  );

  const withGst = supplier?.vendorQuotations?.grandTotalWithGst ?? 0;
  const withoutGst = supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

  setAmountCell(
    shortSheet.getCell(`${startLetter}${summaryStartRow}`),
    withGst - withoutGst
  );
});

/* ---------- TOTAL QUOTATION VALUE ---------- */
summaryStartRow += 1;

shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
shortSheet.getCell(`A${summaryStartRow}`).value =
  "Total Quotation Value (Incl. GST) :";
shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
shortSheet.getCell(`A${summaryStartRow}`).alignment = {
  horizontal: "right",
  vertical: "middle",
};
shortSheet.getCell(`A${summaryStartRow}`).fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  // Merge vendor columns
  shortSheet.mergeCells(
    `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
  );

  const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);

  // Set value
  setAmountCell(
    cell,
    supplier?.vendorQuotations?.grandTotalWithGst ?? "-"
  );

  // Apply alignment
  cell.alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  // APPLY GREY BACKGROUND TO MERGED CELL
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD9D9D9" },
  };
});


/* ---------- DIFFERENCE FROM LOWEST QUOTE ---------- */
summaryStartRow += 1;

// Label (A–E)
shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
shortSheet.getCell(`A${summaryStartRow}`).value =
  "Difference from Lowest Quote :";
shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
shortSheet.getCell(`A${summaryStartRow}`).alignment = {
  horizontal: "right",
  vertical: "middle",
};

// 🔹 L1 = lowest total without GST (must already be calculated)
// const l1Total =
//   Math.min(
//     ...sortedSuppliers
//       .filter((s) => s.quotationReceived)
//       .map(
//         (s) =>
//           s?.vendorQuotations?.grandTotalWithoutGst ?? Infinity
//       )
//   ) || 0;

// Vendor values (F onwards)
sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = shortStartCol + supplierIndex * 3;
  const startLetter = shortSheet.getColumn(colStart).letter;
  const endLetter = shortSheet.getColumn(colStart + 2).letter;

  shortSheet.mergeCells(
    `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
  );

  const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);

  if (!supplier.quotationReceived) {
    cell.value = "Quotation Not Submitted";
  } else {
    const currentTotal =
      supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

    if (currentTotal === l1Total) {
      cell.value = "Lowest Quote";
      cell.font = { italic: true };
    } else {
      const diff = currentTotal - l1Total;

      const percent =
        l1Total > 0 ? (diff / l1Total) * 100 : 0;

      // ₹ + % combined text
      cell.value = `Rs. ${Number(diff).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      })} (${percent.toFixed(2)}%)`;
    }
  }

  cell.alignment = {
    horizontal: "right",
    vertical: "middle",
    wrapText: true,
  };
});


/* ---------- PAYMENT TERMS ---------- */
const hasAnyPaymentTerms = sortedSuppliers.some((supplier) => {
  if (!supplier.quotationReceived) return false;

  const lastSet =
    supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

  const text = lastSet?.agreedToPaymentTerms
    ? htmlToText(rfqData?.paymentTerms)
    : htmlToText(lastSet?.counterPaymentTerms);

  return !!text && text !== "-";
});


if (hasAnyPaymentTerms) {
  summaryStartRow += 1;

  // Label A–E
  shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
  shortSheet.getCell(`A${summaryStartRow}`).value = "Payment Terms :";
  shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
  shortSheet.getCell(`A${summaryStartRow}`).alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;
    const startLetter = shortSheet.getColumn(colStart).letter;
    const endLetter = shortSheet.getColumn(colStart + 2).letter;

    shortSheet.mergeCells(
      `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
    );

    const lastSet =
      supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

    const text =
      lastSet?.agreedToPaymentTerms
        ? htmlToText(rfqData?.paymentTerms)
        : htmlToText(lastSet?.counterPaymentTerms) || "-";

    const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
    cell.value = text;
    cell.alignment = {
      wrapText: true,
      vertical: "top",
      horizontal: "left",
    };

    // AUTO HEIGHT (THIS IS THE KEY)
    applyAutoRowHeight(shortSheet, summaryStartRow, text, {
      mergedCols: 3,
      columnWidth: 15,
    });
  });
}



/* ---------- VENDOR NOTES ---------- */
const hasAnyVendorNotes = sortedSuppliers.some((supplier) => {
  if (!supplier.quotationReceived) return false;

  const text =
    supplier.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes;

  return !!text && text !== "-";
});


if (hasAnyVendorNotes) {
  summaryStartRow += 1;

  // Label A–E
  shortSheet.mergeCells(`A${summaryStartRow}:E${summaryStartRow}`);
  shortSheet.getCell(`A${summaryStartRow}`).value = "Vendor Notes :";
  shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
  shortSheet.getCell(`A${summaryStartRow}`).alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = shortStartCol + supplierIndex * 3;
    const startLetter = shortSheet.getColumn(colStart).letter;
    const endLetter = shortSheet.getColumn(colStart + 2).letter;

    shortSheet.mergeCells(
      `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
    );

    const text =
      supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes || "-";

    const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
    cell.value = text;
    cell.alignment = {
      wrapText: true,
      vertical: "top",
      horizontal: "left",
    };

    // AUTO HEIGHT
    applyAutoRowHeight(shortSheet, summaryStartRow, text, {
      mergedCols: 3,
      columnWidth: 15,
    });
  });
}

/* ---------- BORDERS ---------- */
addVerticalSeparators(shortSheet, {
  lastRow: summaryStartRow,
  staticEndCol: 5,
  vendorStartCol: shortStartCol,
  vendorColSpan: 3,
  vendorCount: sortedSuppliers.length,
});

applyOuterBorderToRange(shortSheet, summaryStartRow);



    const buffer = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buffer]));
    const link = document.createElement("a");
    link.href = url;
    const fileName = `${rfqData?.name?.substring(0, 25) || "Export"}_${rfqData?.rfqType || ""}_${rfqData?.rfqId || ""}.xlsx`;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);

    enqueueSnackbar("Excel downloaded successfully!", {
      variant: "success",
      autoHideDuration: 3000,
    });
  } catch (error) {
    console.error("Error generating labour comparison Excel:", error);
    enqueueSnackbar("Failed to download Excel file.", {
      variant: "error",
      autoHideDuration: 3000,
    });
  } finally {
    setExcelLoading(false);
    setLoadingMessage("");
  }
};

export const handleDownloadContractorComparisonExcel = async (
  rfqData,
  setExcelLoading,
  setLoadingMessage,
  enqueueSnackbar
) => {
  const highlightCell = (cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD6F5D6" }, // light green
    };
  };
  
  const setAmountCell = (cell, value) => {
    if (value === null || value === undefined || value === "" || isNaN(value)) {
      cell.value = "-";
      return;
    }
    cell.value = Number(value);
    cell.numFmt = "#,##0.00";
  };

  const chargeLabels = [
    { label: "Transport Charges :", key: "transport" },
    { label: "Mathade Charges :", key: "mathadeCharges" },
    { label: "Loading Charges :", key: "loading" },
    { label: "Unloading Charges :", key: "unLoading" },
    { label: "Miscellaneous Charges :", key: "miscCharges" },
  ];
  
  
  try {
    // Include ALL suppliers (both with and without quotations)
    const allSuppliers = rfqData?.suppliers || [];

    // Separate suppliers into two groups: those with quotations and those without
    const suppliersWithQuotations = allSuppliers.filter(
      (supplier) => supplier?.quotationReceived
    );
    const suppliersWithoutQuotations = allSuppliers.filter(
      (supplier) => !supplier?.quotationReceived
    );

    // Sort only the suppliers with quotations by rank
    const sortedSuppliersWithQuotes = [...suppliersWithQuotations].sort(
      (a, b) => {
        const rankA = a.vendorQuotations?.rank;
        const rankB = b.vendorQuotations?.rank;
        return parseInt(rankA.substring(1)) - parseInt(rankB.substring(1));
      }
    );

    // Combine sorted suppliers with quotes and unsorted suppliers without quotes
    const sortedSuppliers = [
      ...sortedSuppliersWithQuotes,
      ...suppliersWithoutQuotations,
    ];

    const awardedSuppliersAmount = rfqData?.suppliers
      ?.filter((supplier) => supplier.isAwarded)
      .reduce((total, supplier) => {
        const amount = supplier?.closingDetails?.[0]?.amount || 0;
        return total + amount;
      }, 0);

    const diff = rfqData?.rfqBudget - awardedSuppliersAmount;
    const isOverBudget = diff < 0;
    const formattedDiff = new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
    }).format(Math.abs(diff));
    // const budgetDeviationText = `Budget Deviation: Rs. ${
    //   formattedDiff || "NA"
    // } ${isOverBudget ? "▲" : "▼"}`;

    setExcelLoading(true);
    setLoadingMessage("Generating Contractor Comparison Excel...");

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Full Comparison");

    // Set Column Widths (Adjust as needed)
    sheet.columns = [
      { header: "Sr.", key: "sr", width: 5 },
      { header: "Description", key: "description", width: 45 },
      { header: "Quantity", key: "quantity", width: 12 },
      { header: "Unit", key: "unit", width: 10 },
      { header: "Preferred Brands", key: "brands", width: 30 },
    ];

    let budgetLine = "";

    if (typeof rfqData?.rfqBudget === "number") {
      budgetLine = `RFQ Budget: Rs. ${new Intl.NumberFormat("en-IN").format(
        rfqData.rfqBudget
      )}\n`;
    }    

    // Merge Header Cells & Add RFQ Details
    const headerRange = "A1:E1";
    sheet.mergeCells(headerRange);
    const rfqDetails =
      `Project Name: ${rfqData.projectName}\n` +
      `Project Location: ${rfqData.location}\n` +
      `Neevay RFQ ID: ${rfqData.rfqId}\n` +
      `RFQ Date: ${new Date(rfqData.createdAt).toLocaleDateString("en-GB")}\n` +
      `Neevay RFQ Subject: ${rfqData.name}\n` +
      `Total Quotations: ${suppliersWithQuotations.length}\n` +
      `Total Invited Vendors: ${allSuppliers.length}\n` + // Added total invited vendors count
      budgetLine;

    sheet.getCell("A1").value = rfqDetails;
    sheet.getCell("A1").alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true,
    };
    sheet.getCell("A1").font = { bold: true };

    // Add Headers in Row 2
    const headerRow = sheet.getRow(2);
    headerRow.values = [
      "Sr.",
      "Description",
      "Quantity",
      "Unit",
      "Preferred Brands",
    ];
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    // Populate Providing & Fixing Details (Starting Row 3)
    let rowIndex = 3;
    rfqData.providingFixingDetails.forEach((section, secIndex) => {
      const sectionRow = sheet.getRow(rowIndex++);
      sectionRow.getCell(1).value = `S${secIndex + 1}`;
      sectionRow.getCell(2).value = section.sectionName;
      sectionRow.font = { bold: true };

      section.scopes.forEach((scope, scopeIndex) => {
        const scopeRow = sheet.getRow(rowIndex++);
        scopeRow.getCell(1).value = scopeIndex + 1;
        scopeRow.getCell(2).value = scope?.scopeOfWork?.replace(/<[^>]*>/g, "");
        scopeRow.getCell(3).value = scope?.quantity;
        scopeRow.getCell(4).value = getFormattedUnit(scope.unit);
        scopeRow.getCell(5).value = scope?.preferredBrands
          ?.join(", ")
          ?.replace(/<[^>]*>/g, "");

        scope.materials.forEach((material, matIndex) => {
          const materialRow = sheet.getRow(rowIndex++);
          materialRow.getCell(1).value = `${scopeIndex + 1}.${matIndex + 1}`;
          materialRow.getCell(2).value = material?.name?.replace(
            /<[^>]*>/g,
            ""
          );
          materialRow.getCell(3).value = material.quantity;
          materialRow.getCell(4).value = material.unit;
          materialRow.getCell(5).value = material?.brands
            ?.join(", ")
            ?.replace(/<[^>]*>/g, "");
        });
      });
    });

    // Add Supplier Details
    let startCol = 6; // Column F
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = startCol + supplierIndex * 7;
      const colLetter = sheet.getColumn(colStart).letter;

      // Merge Cells for Supplier Header
      const supplierHeaderRange = `${colLetter}1:${
        sheet.getColumn(colStart + 6).letter
      }1`;
      sheet.mergeCells(supplierHeaderRange);

      // Different header for suppliers without quotations
      const rfqBudget = rfqData.rfqBudget;
      const totalWithGst = supplier?.vendorQuotations?.grandTotalWithGst;
      const totalWithoutGst = supplier?.vendorQuotations?.grandTotalWithoutGst;
      const totalGst = totalWithGst - totalWithoutGst;

      const deviation = (rfqBudget - totalWithGst) + totalGst;

      const isOverBudget = deviation < 0;

// const deviationText = supplier.quotationReceived
//   ? `Budget Deviation: Rs. ${Math.abs(deviation).toLocaleString("en-IN")} ${
//       isOverBudget ? "▲" : "▼"
//     }\n\n`
//   : "";
  let deviationText = "";

  if (
    supplier.quotationReceived &&
    hasValidBudget(rfqData) &&
    typeof supplier?.vendorQuotations?.grandTotalWithGst === "number"
  ) {  
  const deviation =
    rfqData.rfqBudget -
    supplier.vendorQuotations.grandTotalWithGst;

  const isOverBudget = deviation < 0;

  deviationText =
    `Budget Deviation: Rs. ${Math.abs(deviation).toLocaleString("en-IN")} ${
      isOverBudget ? "▲" : "▼"
    }\n\n`;
}


const supplierDetails = supplier.quotationReceived
  ? `Vendor Name: ${supplier.businessName}\n` +
    `Vendor Rank: ${supplier.vendorQuotations?.rank || "N/A"}\n` +
    `Vendor Phone: ${supplier.contactDetails[0]?.mobile}\n\n` +
    `Vendor Email: ${supplier.contactDetails[0]?.email}\n\n` +
    `Material/Installation Cost: Rs. ${new Intl.NumberFormat(
      "en-IN"
    ).format(
      supplier?.vendorQuotations?.quotationSets?.[0]?.totalWithoutGst || 0
    )}\n` +
    `Total Quotation Amount (Inc. GST): Rs. ${new Intl.NumberFormat(
      "en-IN"
    ).format(totalWithGst)}\n\n` +
    deviationText +
    `Status: Submitted Quotation`
  : `Vendor Name: ${supplier.businessName}\n` +
    `Vendor Phone: ${supplier.contactDetails[0]?.mobile}\n\n` +
    `Vendor Email: ${supplier.contactDetails[0]?.email}\n\n` +
    `Status: Did Not Submit Quotation`;


      sheet.getCell(`${colLetter}1`).value = supplierDetails;
      sheet.getCell(`${colLetter}1`).alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
      };
      sheet.getCell(`${colLetter}1`).font = { bold: true };

      sheet
        .getRow(2)
        .splice(
          colStart,
          0,
          "Material Rate",
          "Installation Rate",
          "GST",
          "Material Cost",
          "Installation Cost",
          "Total(Excl. GST)",
          "Vendor Remarks"
        );

      // Set column widths for these new columns (starting from colStart)
      sheet.getColumn(colStart).width = 12; // Material Rate
      sheet.getColumn(colStart + 1).width = 12; // Installation Rate
      sheet.getColumn(colStart + 2).width = 7; // GST
      sheet.getColumn(colStart + 3).width = 15; // Material Cost
      sheet.getColumn(colStart + 4).width = 15; // Installation Cost
      sheet.getColumn(colStart + 5).width = 20; // Total(Excl. GST)
      sheet.getColumn(colStart + 6).width = 40;

      // Populate Quotation Data
      let dataRowIndex = 3;

      if (supplier.quotationReceived) {
        supplier?.vendorQuotations?.quotationSets
          ?.slice(-1)[0]
          ?.contractorDetails?.forEach((section) => {
            const sectionRow = sheet.getRow(dataRowIndex++);
            sectionRow.getCell(colStart + 5).value = new Intl.NumberFormat(
              "en-IN"
            ).format(section?.sectionTotalExclGst);

            section?.scopes?.forEach((scope) => {
              const scopeRow = sheet?.getRow(dataRowIndex++);

              if (scope?.materials && scope?.materials?.length > 0) {
                // Empty the price fields for this scope row
                scopeRow.getCell(colStart).value = "";
                scopeRow.getCell(colStart + 1).value = "";
                scopeRow.getCell(colStart + 2).value = "";
                scopeRow.getCell(colStart + 3).value = "";
                scopeRow.getCell(colStart + 4).value = "";
                scopeRow.getCell(colStart + 5).value = "";
                scopeRow.getCell(colStart + 6).value = "";
              } else {
                // If no materials, populate price data for scope itself
                scopeRow.getCell(colStart).value = new Intl.NumberFormat(
                  "en-IN"
                ).format(scope.basePrice || 0);
                scopeRow.getCell(colStart + 1).value = new Intl.NumberFormat(
                  "en-IN"
                ).format(scope.installationRate || 0);
                scopeRow.getCell(colStart + 2).value = `${scope.gst || 0}%`;
                scopeRow.getCell(colStart + 3).value = new Intl.NumberFormat(
                  "en-IN"
                ).format(scope.supplyCost || 0);
                scopeRow.getCell(colStart + 4).value = new Intl.NumberFormat(
                  "en-IN"
                ).format(scope.installationCost || 0);
                scopeRow.getCell(colStart + 5).value = new Intl.NumberFormat(
                  "en-IN"
                ).format(scope.totalExclGst || 0);
                scopeRow.getCell(colStart + 6).value =
                  scope?.comments?.replace(/<[^>]*>/g, "") || "";
                scopeRow.getCell(colStart + 6).alignment = { wrapText: true };
              }

              scope.materials.forEach((material) => {
                const materialRow = sheet.getRow(dataRowIndex++);
                materialRow.getCell(colStart).value = new Intl.NumberFormat(
                  "en-IN"
                ).format(material.basePrice || 0);
                materialRow.getCell(colStart + 1).value = new Intl.NumberFormat(
                  "en-IN"
                ).format(material.installationRate || 0);
                materialRow.getCell(colStart + 2).value = `${
                  material.gst || 0
                }%`;
                materialRow.getCell(colStart + 3).value = new Intl.NumberFormat(
                  "en-IN"
                ).format(material.netPrice || 0);
                materialRow.getCell(colStart + 4).value = new Intl.NumberFormat(
                  "en-IN"
                ).format(material.installationCost || 0);
                materialRow.getCell(colStart + 5).value = new Intl.NumberFormat(
                  "en-IN"
                ).format(material.totalExclGst || 0);
                materialRow.getCell(colStart + 6).value =
                  material?.remarks?.replace(/<[^>]*>/g, "") || "";
                materialRow.getCell(colStart + 6).alignment = {
                  wrapText: true,
                };
              });
            });
          });
      } else {
        // For vendors who didn't submit quotations, leave all cells empty with "-"
        for (let i = 3; i <= rowIndex; i++) {
          const row = sheet.getRow(i);
          for (let j = 0; j < 7; j++) {
            row.getCell(colStart + j).value = "-";
            row.getCell(colStart + j).alignment = { horizontal: "center" };
          }
        }
      }

      // Adjust Row Height Based on Content
      const lineCount = supplierDetails.split("\n").length;
      sheet.getRow(1).height = Math.max(20, lineCount * 20);
    });

    // [Rest of your existing code for Terms of Contract sheet and file download...]
    // Add Terms of Contract Sheet
    const termsSheet = workbook.addWorksheet("Terms of Contract");

    // Set header and terms content
    termsSheet.getCell("A1").value = "Terms of Contract";
    termsSheet.getCell("A1").font = { bold: true };
    termsSheet.getCell("A1").alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    termsSheet.getCell("A2").value = rfqData?.termsAndConditions?.replace(
      /<[^>]*>/g,
      ""
    );
    termsSheet.getCell("A2").alignment = { wrapText: true };
    termsSheet.getColumn(1).width = 100;

    /* ===================== CONTRACTOR SUMMARY VIEW ===================== */

const summarySheet = workbook.addWorksheet("Contractor_Summary_View");

// ---------- Column Setup ----------
summarySheet.columns = [
  { width: 5 },   // A - Sr
  { width: 45 },  // B - Description
  { width: 12 },  // C - Quantity
  { width: 10 },  // D - Unit
  { width: 18 },  // E - Lowest Material Rate
  ...Array(sortedSuppliers.length * 3).fill({ width: 18 }), // Vendors
];

const applyGreyHeaderStyle = (cell) => {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFEFEF" }, // light grey
  };
  cell.font = { bold: true };
};

const applyGreyRowStyle = (row, fromCol, toCol) => {
  for (let col = fromCol; col <= toCol; col++) {
    const cell = row.getCell(col);
    applyGreyHeaderStyle(cell);
  }
};

const applyLightGreyRowStyle = (row, fromCol, toCol) => {
  for (let col = fromCol; col <= toCol; col++) {
    const cell = row.getCell(col);
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF7F7F7" }, // very light grey
    };
  }
};


// Right border helper
const applyRightBorder = (cell) => {
  cell.border = {
    ...(cell.border || {}),
    right: { style: "thin" },
  };
};

// Bottom border helper
const applyBottomBorder = (cell) => {
  cell.border = {
    ...(cell.border || {}),
    bottom: { style: "thin" },
  };
};



// ---------- SUMMARY HEADER (ROW 1) ----------
// A–E : Project info
summarySheet.mergeCells("A1:E1");
summarySheet.getCell("A1").value =
  `Project Name: ${rfqData.projectName}\n` +
  `RFQ Name: ${rfqData.name}\n` +
  `RFQ Type: Contractor`;

summarySheet.getCell("A1").font = { bold: true };
summarySheet.getCell("A1").alignment = {
  wrapText: true,
  vertical: "middle",
  horizontal: "left",
};

// F–H : RFQ meta info
summarySheet.mergeCells("F1:H1");
summarySheet.getCell("F1").value =
  `RFQ ID: ${rfqData.rfqId}\n` +
  `Created By: ${rfqData.createdBy || "-"}\n` +
  `Created At: ${
    rfqData.createdAt
      ? new Date(rfqData.createdAt).toLocaleDateString("en-GB")
      : "-"
  }`;

summarySheet.getCell("F1").font = { bold: true };
summarySheet.getCell("F1").alignment = {
  wrapText: true,
  vertical: "middle",
  horizontal: "left",
};

// Adjust row height
summarySheet.getRow(1).height = 60;


summarySheet.getCell("A1").font = { bold: true };
summarySheet.getCell("A1").alignment = {
  wrapText: true,
  vertical: "middle",
  horizontal: "left",
};

// ---------- Vendor Headers ----------
let summaryStartCol = 6;

sortedSuppliers.forEach((supplier, i) => {
  const colStart = summaryStartCol + i * 3;
  const start = summarySheet.getColumn(colStart).letter;
  const end = summarySheet.getColumn(colStart + 2).letter;

  summarySheet.mergeCells(`${start}2:${end}2`);
  summarySheet.getCell(`${start}2`).value =
    `${supplier.businessName} (${supplier.vendorQuotations?.rank || "-"})`;

  summarySheet.getCell(`${start}2`).font = { bold: true };
  summarySheet.getCell(`${start}2`).alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };

});

// ---------- BUDGET DEVIATION ROW (ROW 3) ----------
if (hasValidBudget(rfqData)) {
  const rfqBudget = rfqData.rfqBudget;

  // Label A–E
  summarySheet.mergeCells("A3:E3");
  const budgetLabelCell = summarySheet.getCell("A3");

  budgetLabelCell.value = `RFQ Budget: Rs. ${new Intl.NumberFormat(
    "en-IN"
  ).format(rfqBudget)}`;

  budgetLabelCell.font = { bold: true };
  budgetLabelCell.alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  // ---------- BUDGET DEVIATION (RFQ - TOTAL + GST) ----------
  sortedSuppliers.forEach((supplier, i) => {
    const colStart = summaryStartCol + i * 3;
    const start = summarySheet.getColumn(colStart).letter;
    const end = summarySheet.getColumn(colStart + 2).letter;

    summarySheet.mergeCells(`${start}3:${end}3`);
    const cell = summarySheet.getCell(`${start}3`);

    if (!supplier.quotationReceived) {
      cell.value = "-";
      cell.alignment = { horizontal: "center", vertical: "middle" };
      return;
    }

    const totalInclGst =
      supplier?.vendorQuotations?.grandTotalWithGst ?? 0;

    const totalExclGst =
      supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

    // GST
    const totalGst = totalInclGst - totalExclGst;

    // SAME FORMULA
    const deviation = (rfqBudget - totalInclGst) + totalGst;
    const isOverBudget = deviation < 0;

    cell.value = `Rs. ${Math.abs(deviation).toLocaleString("en-IN")} ${
      isOverBudget ? "▲" : "▼"
    }`;

    cell.font = {
      bold: true,
      color: {
        argb: isOverBudget ? "FFFF0000" : "FF008000",
      },
    };

    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
  });
}



// Light grey background
// applyLightGreyRowStyle(
//   budgetRow,
//   1,
//   5 + sortedSuppliers.length * 3
// );


const getLowestTotal = (values) => {
  const valid = values.filter(
    (v) => typeof v === "number" && !isNaN(v)
  );
  return valid.length ? Math.min(...valid) : "-";
};


// ---------- Column Titles ----------
const summaryHeaderRow = summarySheet.getRow(4);

summaryHeaderRow.values = [
  "Sr.",
  "Description",
  "Quantity",
  "Unit",
  "Lowest Amount",
];

sortedSuppliers.forEach((_, i) => {
  const colStart = summaryStartCol + i * 3;

  const materialCell = summarySheet.getRow(4).getCell(colStart);
  materialCell.value = "Material\nRate";
  materialCell.alignment = {
    wrapText: true,
    horizontal: "center",
    vertical: "middle",
  };

  const installationCell = summarySheet.getRow(4).getCell(colStart + 1);
  installationCell.value = "Installation\nRate";
  installationCell.alignment = {
    wrapText: true,
    horizontal: "center",
    vertical: "middle",
  };

  const totalCell = summarySheet.getRow(4).getCell(colStart + 2);
  totalCell.value = "Total\n(Excl. GST)";
  totalCell.alignment = {
    wrapText: true,
    horizontal: "center",
    vertical: "middle",
  };
});


// Font once
summaryHeaderRow.font = { bold: true };

// Grey background + borders ONCE
applyGreyRowStyle(
  summaryHeaderRow,
  1,
  5 + sortedSuppliers.length * 3
);


// ---------- Line Items ----------
let summaryRow = 5;

rfqData.providingFixingDetails.forEach((section, secIndex) => {
  // Section Row
  const sectionRow = summarySheet.getRow(summaryRow++);
  sectionRow.getCell(1).value = `S${secIndex + 1}`;
  sectionRow.getCell(2).value = section.sectionName;
  sectionRow.font = { bold: true };

  section.scopes.forEach((scope, scopeIndex) => {
    // Scope Row
    const scopeRow = summarySheet.getRow(summaryRow++);
    scopeRow.getCell(1).value = scopeIndex + 1;
    scopeRow.getCell(2).value = scope.scopeOfWork.replace(/<[^>]*>/g, "");
    scopeRow.getCell(3).value = scope.quantity;
    scopeRow.getCell(4).value = getFormattedUnit(scope.unit);
   
    
    const materialRates = [];

    sortedSuppliers.forEach((supplier, i) => {
      const colStart = summaryStartCol + i * 3;

      if (!supplier.quotationReceived) {
        scopeRow.getCell(colStart).value = "-";
        scopeRow.getCell(colStart + 1).value = "-";
        scopeRow.getCell(colStart + 2).value = "-";
        return;
      }

      const lastSet =
        supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

      const scopeData =
        lastSet?.contractorDetails?.[secIndex]?.scopes?.[scopeIndex];

      if (scopeData?.materials?.length > 0) {
        scopeRow.getCell(colStart).value = "";
        scopeRow.getCell(colStart + 1).value = "";
        scopeRow.getCell(colStart + 2).value = "";
        return;
      }

      const total = scopeData?.totalExclGst;

      setAmountCell(scopeRow.getCell(colStart), scopeData?.basePrice);
      setAmountCell(scopeRow.getCell(colStart + 1), scopeData?.installationRate);
      setAmountCell(scopeRow.getCell(colStart + 2), total);
      
      if (typeof total === "number") {
        materialRates.push({ total, colStart });
      }
      
    });

    // LOWEST MATERIAL RATE (COLUMN E)
    if (materialRates.length > 0) {
      const lowest = Math.min(...materialRates.map(r => r.total));
    
      materialRates.forEach(({ total, colStart }) => {
        if (total === lowest) {
          highlightCell(scopeRow.getCell(colStart + 2)); // TOTAL column
        }
      });
    
      setAmountCell(scopeRow.getCell(5), lowest); // Column E
    } else {
      scopeRow.getCell(5).value = "-";
    }
    


    // Material Rows
    scope.materials.forEach((material, matIndex) => {
      const matRow = summarySheet.getRow(summaryRow++);
      matRow.getCell(1).value = `${scopeIndex + 1}.${matIndex + 1}`;
      matRow.getCell(2).value = material.name.replace(/<[^>]*>/g, "");
      matRow.getCell(3).value = material.quantity;
      matRow.getCell(4).value = material.unit;
    
      const materialRates = [];
    
      // ---------- MATERIAL RATE ROW ----------
      sortedSuppliers.forEach((supplier, i) => {
        const colStart = summaryStartCol + i * 3;
    
        if (!supplier.quotationReceived) {
          matRow.getCell(colStart).value = "-";
          matRow.getCell(colStart + 1).value = "-";
          matRow.getCell(colStart + 2).value = "-";
          return;
        }
    
        const lastSet =
          supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
    
        const matData =
          lastSet?.contractorDetails?.[secIndex]
            ?.scopes?.[scopeIndex]
            ?.materials?.[matIndex];
    
            const total = matData?.totalExclGst;

            setAmountCell(matRow.getCell(colStart), matData?.basePrice);
            setAmountCell(matRow.getCell(colStart + 1), matData?.installationRate);
            setAmountCell(matRow.getCell(colStart + 2), total);

            if (typeof total === "number") {
              materialRates.push({ total, colStart });
            }

      });
    
      // ---------- LOWEST MATERIAL RATE ----------
      if (materialRates.length > 0) {
        const lowest = Math.min(...materialRates.map(r => r.total));
      
        materialRates.forEach(({ total, colStart }) => {
          if (total === lowest) {
            highlightCell(matRow.getCell(colStart + 2)); // TOTAL column
          }
        });
      
        setAmountCell(matRow.getCell(5), lowest);
      } else {
        matRow.getCell(5).value = "-";
      }
      
      
      // ---------- MATERIAL REMARKS ROW (ONLY IF REMARK EXISTS) ----------
// 1️ Collect all remark texts first
      const remarkTexts = [];

      sortedSuppliers.forEach((supplier) => {
        if (!supplier.quotationReceived) return;

        const lastSet =
          supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

        const matData =
          lastSet?.contractorDetails?.[secIndex]
            ?.scopes?.[scopeIndex]
            ?.materials?.[matIndex];

        const text =
          matData?.remarks
            ?.replace(/<br\s*\/?>/gi, "\n")
            ?.replace(/<\/p>/gi, "\n")
            ?.replace(/<[^>]*>/g, "")
            ?.trim();

        if (text) remarkTexts.push(text);
      });

      // If NO remarks → do NOT create row
      if (remarkTexts.length === 0) return;

      // 2️ Create remarks row
      const remarksRow = summarySheet.getRow(summaryRow++);

      // Clear static columns
      [1, 2, 3, 4, 5].forEach((c) => {
        remarksRow.getCell(c).value = "";
      });

      // 3️ Add vendor remarks (MERGED 3 COLS)
      sortedSuppliers.forEach((supplier, i) => {
        const colStart = summaryStartCol + i * 3;
        const startLetter = summarySheet.getColumn(colStart).letter;
        const endLetter = summarySheet.getColumn(colStart + 2).letter;

        summarySheet.mergeCells(
          `${startLetter}${remarksRow.number}:${endLetter}${remarksRow.number}`
        );

        const cell = remarksRow.getCell(colStart);

        if (!supplier.quotationReceived) {
          cell.value = "-";
          cell.alignment = { horizontal: "center", vertical: "top" };
          return;
        }

        const lastSet =
          supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

        const matData =
          lastSet?.contractorDetails?.[secIndex]
            ?.scopes?.[scopeIndex]
            ?.materials?.[matIndex];

        const remarks =
          matData?.remarks
            ?.replace(/<br\s*\/?>/gi, "\n")
            ?.replace(/<\/p>/gi, "\n")
            ?.replace(/<[^>]*>/g, "")
            ?.trim();

        if (!remarks) {
          cell.value = "-";
          return;
        }

        cell.value = remarks;
        cell.alignment = {
          wrapText: true,
          vertical: "top",
          horizontal: "left",
        };
      });

      // 4 CALCULATE HEIGHT ONCE (based on widest merged cell)
      const longestText = remarkTexts.reduce(
        (a, b) => (b.length > a.length ? b : a),
        ""
      );

      // Approx calculation:
      // merged width = 3 cols × 18 width ≈ 54 chars per line
      const approxCharsPerLine = 54;
      const lineCount = Math.max(
        longestText.split("\n").length,
        Math.ceil(longestText.length / approxCharsPerLine)
      );

      // Excel line height ≈ 18
      remarksRow.height = Math.max(24, lineCount * 18);

          });
          
        });

        // ---------- SECTION TOTAL ROW ----------
      const sectionTotalRow = summarySheet.getRow(summaryRow++);

      // Label column (A–E)
      sectionTotalRow.getCell(5).value = "Total";
      sectionTotalRow.getCell(5).font = { bold: true };
      sectionTotalRow.getCell(5).alignment = {
        horizontal: "right",
        vertical: "middle",
      };

      // Clear unused static columns
      [1, 2, 3, 4].forEach((c) => {
        sectionTotalRow.getCell(c).value = "";
      });


// Vendor totals
sortedSuppliers.forEach((supplier, i) => {
  const colStart = summaryStartCol + i * 3;
  const startLetter = summarySheet.getColumn(colStart).letter;
  const endLetter = summarySheet.getColumn(colStart + 2).letter;

  summarySheet.mergeCells(
    `${startLetter}${sectionTotalRow.number}:${endLetter}${sectionTotalRow.number}`
  );

  const cell = sectionTotalRow.getCell(colStart);

  if (!supplier.quotationReceived) {
    cell.value = "-";
    cell.alignment = { horizontal: "right", vertical: "middle" };
    return;
  }

  const lastSet =
    supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

  const sectionTotal =
    lastSet?.contractorDetails?.[secIndex]?.sectionTotalExclGst ?? 0;

  setAmountCell(cell, sectionTotal);

  cell.font = { bold: true };
  cell.alignment = {
    horizontal: "right",
    vertical: "middle",
  };
  applyLightGreyRowStyle(
    sectionTotalRow,
    1,
    5 + sortedSuppliers.length * 3
  );
  
});

});

// ---------- SUMMARY TOTALS ----------

const addSummaryRow = (
  label,
  valueGetter,
  isText = false,
  applyGrey = false
) => {
  summarySheet.mergeCells(`A${summaryRow}:E${summaryRow}`);
  const labelCell = summarySheet.getCell(`A${summaryRow}`);
  labelCell.value = label;
  labelCell.font = { bold: true };
  labelCell.alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  sortedSuppliers.forEach((supplier, i) => {
    const colStart = summaryStartCol + i * 3;
    const start = summarySheet.getColumn(colStart).letter;
    const end = summarySheet.getColumn(colStart + 2).letter;

    summarySheet.mergeCells(`${start}${summaryRow}:${end}${summaryRow}`);
    const cell = summarySheet.getCell(`${start}${summaryRow}`);
    const value = valueGetter(supplier);

    if (isText) {
      cell.value = value || "-";
      cell.alignment = { wrapText: true };
    } else {
      setAmountCell(cell, value);
    }
  });

  // 🔹 APPLY GREY STYLE (like Material/Labour)
  if (applyGrey) {
    const row = summarySheet.getRow(summaryRow);
    applyGreyRowStyle(
      row,
      1,
      5 + sortedSuppliers.length * 3
    );
  }

  summaryRow++;
};


// ---------- OTHER CHARGES ----------
chargeLabels.forEach((charge) => {
  const hasAnyCharge = sortedSuppliers.some((supplier) => {
    if (!supplier.quotationReceived) return false;

    return (
      supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
        ?.otherCharges?.[charge.key]?.mrp > 0
    );
  });

  // Skip row if no vendor has this charge
  if (!hasAnyCharge) return;

  summarySheet.mergeCells(`A${summaryRow}:E${summaryRow}`);
  const labelCell = summarySheet.getCell(`A${summaryRow}`);
  labelCell.value = charge.label;
  labelCell.font = { bold: true };
  labelCell.alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  sortedSuppliers.forEach((supplier, supplierIndex) => {
    const colStart = summaryStartCol + supplierIndex * 3;
    const startLetter = summarySheet.getColumn(colStart).letter;
    const endLetter = summarySheet.getColumn(colStart + 2).letter;

    summarySheet.mergeCells(
      `${startLetter}${summaryRow}:${endLetter}${summaryRow}`
    );

    const cell = summarySheet.getCell(`${startLetter}${summaryRow}`);

    if (!supplier.quotationReceived) {
      cell.value = "-";
    } else {
      const value =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
          ?.otherCharges?.[charge.key]?.mrp ?? 0;

      setAmountCell(cell, value);
    }

    cell.alignment = {
      horizontal: "right",
      vertical: "middle",
    };
  });

  summaryRow++;
});


// TOTAL BASIC AMOUNT
addSummaryRow(
  "Total Basic Amount",
  (s) => s?.vendorQuotations?.grandTotalWithoutGst,
  false,
  true // 👈 THIS MAKES IT GREY + BORDER
);


// TOTAL GST
addSummaryRow(
  "Total GST",
  (s) => {
    const withGst = s?.vendorQuotations?.grandTotalWithGst;
    const withoutGst = s?.vendorQuotations?.grandTotalWithoutGst;

    if (typeof withGst !== "number" || typeof withoutGst !== "number") {
      return 0;
    }

    return withGst - withoutGst;
  }
);


// TOTAL QUOTATION VALUE
addSummaryRow(
  "Total Quotation Value (Incl. GST)",
  (s) => s?.vendorQuotations?.grandTotalWithGst,
  false,
  true // 👈 THIS MAKES IT GREY + BORDER
);


const l1Total =
  Math.min(
    ...sortedSuppliers
      .filter((s) => s.quotationReceived)
      .map(
        (s) =>
          s?.vendorQuotations?.grandTotalWithoutGst ?? Infinity
      )
  ) || 0;
// ---------- DIFFERENCE FROM LOWEST QUOTE ----------
summarySheet.mergeCells(`A${summaryRow}:E${summaryRow}`);

const labelCell = summarySheet.getCell(`A${summaryRow}`);
labelCell.value = "Difference from Lowest Quote :";
labelCell.font = { bold: true };
labelCell.alignment = {
  horizontal: "right",
  vertical: "middle",
};

// Vendor values (F onwards)
sortedSuppliers.forEach((supplier, supplierIndex) => {
  const colStart = summaryStartCol + supplierIndex * 3;
  const startLetter = summarySheet.getColumn(colStart).letter;
  const endLetter = summarySheet.getColumn(colStart + 2).letter;

  summarySheet.mergeCells(
    `${startLetter}${summaryRow}:${endLetter}${summaryRow}`
  );

  const cell = summarySheet.getCell(`${startLetter}${summaryRow}`);

  if (!supplier.quotationReceived) {
    cell.value = "Quotation Not Submitted";
  } else {
    const currentTotal =
      supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

    if (currentTotal === l1Total) {
      cell.value = "Lowest Quote";
      cell.font = { italic: true };
    } else {
      const diff = currentTotal - l1Total;
      const percent =
        l1Total > 0 ? (diff / l1Total) * 100 : 0;

      cell.value = `Rs. ${Number(diff).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      })} (${percent.toFixed(2)}%)`;
    }
  }

  cell.alignment = {
    horizontal: "right",
    vertical: "middle",
    wrapText: true,
  };
});

summaryRow++;


/* ---------- PAYMENT TERMS ---------- */
const hasAnyPaymentTerms = sortedSuppliers.some((supplier) => {
  if (!supplier.quotationReceived) return false;

  const lastSet =
    supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

  const text = lastSet?.agreedToPaymentTerms
    ? htmlToText(rfqData?.paymentTerms)
    : htmlToText(lastSet?.counterPaymentTerms);

  return !!text && text !== "-";
});


if (hasAnyPaymentTerms) {
  // Label A–E
  summarySheet.mergeCells(`A${summaryRow}:E${summaryRow}`);
  const labelCell = summarySheet.getCell(`A${summaryRow}`);
  labelCell.value = "Payment Terms :";
  labelCell.font = { bold: true };
  labelCell.alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  sortedSuppliers.forEach((supplier, i) => {
    const colStart = summaryStartCol + i * 3;
    const start = summarySheet.getColumn(colStart).letter;
    const end = summarySheet.getColumn(colStart + 2).letter;

    summarySheet.mergeCells(`${start}${summaryRow}:${end}${summaryRow}`);

    const lastSet =
      supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

    const text =
      lastSet?.agreedToPaymentTerms
        ? htmlToText(rfqData?.paymentTerms)
        : htmlToText(lastSet?.counterPaymentTerms) || "-";

    const cell = summarySheet.getCell(`${start}${summaryRow}`);
    cell.value = text;
    cell.alignment = {
      wrapText: true,
      vertical: "top",
      horizontal: "left",
    };

    // AUTO HEIGHT
    applyAutoRowHeight(summarySheet, summaryRow, text, {
      mergedCols: 3,
      columnWidth: 18,
    });
  });

  summaryRow++;
}




/* ---------- VENDOR NOTES ---------- */
const hasAnyVendorNotes = sortedSuppliers.some((supplier) => {
  if (!supplier.quotationReceived) return false;

  const text =
    supplier.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes;

  return !!text && text !== "-";
});


if (hasAnyVendorNotes) {
  // Label A–E
  summarySheet.mergeCells(`A${summaryRow}:E${summaryRow}`);
  const labelCell = summarySheet.getCell(`A${summaryRow}`);
  labelCell.value = "Vendor Notes :";
  labelCell.font = { bold: true };
  labelCell.alignment = {
    horizontal: "right",
    vertical: "middle",
  };

  sortedSuppliers.forEach((supplier, i) => {
    const colStart = summaryStartCol + i * 3;
    const start = summarySheet.getColumn(colStart).letter;
    const end = summarySheet.getColumn(colStart + 2).letter;

    summarySheet.mergeCells(`${start}${summaryRow}:${end}${summaryRow}`);

    const text =
      supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes || "-";

    const cell = summarySheet.getCell(`${start}${summaryRow}`);
    cell.value = text;
    cell.alignment = {
      wrapText: true,
      vertical: "top",
      horizontal: "left",
    };

    // AUTO HEIGHT
    applyAutoRowHeight(summarySheet, summaryRow, text, {
      mergedCols: 3,
      columnWidth: 18,
    });
  });

  summaryRow++;
}

// ---------- VERTICAL BORDERS (E, H, K, ...) ----------
const lastSummaryRow = summaryRow - 1;
const lastCol = 5 + sortedSuppliers.length * 3;

// Column E (static section)
for (let r = 1; r <= lastSummaryRow; r++) {
  applyRightBorder(summarySheet.getRow(r).getCell(5));
}

// After each vendor block (H, K, N, ...)
sortedSuppliers.forEach((_, i) => {
  const borderCol = summaryStartCol + i * 3 + 2; // last col of vendor block

  for (let r = 1; r <= lastSummaryRow; r++) {
    applyRightBorder(summarySheet.getRow(r).getCell(borderCol));
  }
});
// ---------- BOTTOM BORDER BELOW VENDOR NOTES ----------
const vendorNotesRowIndex = summaryRow - 1;

for (let c = 1; c <= 5 + sortedSuppliers.length * 3; c++) {
  applyBottomBorder(
    summarySheet.getRow(vendorNotesRowIndex).getCell(c)
  );
}



/* =================== END CONTRACTOR SUMMARY VIEW =================== */


    // Generate & Download Excel File
    const blob = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = url;
    const fileName = `${rfqData?.name?.substring(0, 25) || "Export"}_${rfqData?.rfqType || ""}_${rfqData?.rfqId || ""}.xlsx`;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);

    enqueueSnackbar("Excel downloaded successfully!", {
      variant: "success",
      autoHideDuration: 3000,
    });
  } catch (error) {
    console.error("Error generating contractor comparison Excel:", error);
    enqueueSnackbar("Failed to download Excel file.", {
      variant: "error",
      autoHideDuration: 3000,
    });
  } finally {
    setExcelLoading(false);
    setLoadingMessage("");
  }
};
